import { useCallback, useEffect, useMemo, useState } from "react";
import { Layout } from "../components/Layout";
import { Panel, Stat, StatusBadge } from "../components/Panel";
import {
  archiveDealWinDeal,
  assessDealWinDeal,
  createDealWinCustomerValueSnapshot,
  createDealWinDeal,
  fetchDealWinAssessments,
  fetchDealWinDeal,
  fetchDealWinDeals,
  fetchLatestDealWinAssessment,
  updateDealWinDeal,
  type DealWinAssessment,
  type DealWinCommunicationPreference,
  type DealWinContactDecisionProfile,
  type DealWinContactRecommendedApproach,
  type DealWinContactRiskTrigger,
  type DealWinDeal,
  type DealWinDealInput,
  type DealWinDealStatus,
  type DealWinDecisionStyle,
  type DealWinHauffeValueContext,
  type DealWinImprovementLever,
  type DealWinProofPreference,
  type DealWinRiskLevel,
  type DealWinSalesStage
} from "../lib/api/dealWinEngineClient";
import { t, useWorkspaceLanguage, withLanguagePrefix } from "../lib/i18n";

const salesStages: DealWinSalesStage[] = ["lead", "qualified", "discovery", "proposal", "negotiation", "verbal_commit", "won", "lost"];
const dealStatuses: DealWinDealStatus[] = ["open", "won", "lost", "disqualified", "archived"];
const decisionStyleOptions: DealWinDecisionStyle[] = ["analytical", "relationship_driven", "authority_driven", "innovation_driven", "risk_averse", "speed_oriented", "consensus_driven"];
const communicationPreferenceOptions: DealWinCommunicationPreference[] = ["numbers_and_facts", "strategic_vision", "operational_detail", "trust_and_relationship", "proof_and_references", "concise_executive_summary"];
const riskTriggerOptions: DealWinContactRiskTrigger[] = ["unclear_roi", "implementation_effort", "internal_politics", "budget_uncertainty", "loss_of_control", "reputational_risk", "missing_references", "unclear_decision_path"];
const proofPreferenceOptions: DealWinProofPreference[] = ["business_case", "customer_reference", "roi_calculation", "implementation_plan", "executive_summary", "technical_validation", "pilot_or_test"];
const recommendedApproachOptions: DealWinContactRecommendedApproach[] = ["lead_with_business_case", "reduce_perceived_risk", "secure_internal_sponsor", "show_reference_or_proof", "clarify_decision_path", "create_executive_summary", "align_on_next_decision"];

const initialForm: DealWinDealInput = {
  title: "",
  companyName: "",
  dealValue: 0,
  currency: "EUR",
  salesStage: "qualified",
  expectedCloseDate: "",
  ownerName: "",
  status: "open",
  contactName: "",
  contactEmail: "",
  industry: "",
  companySize: "",
  decisionMakerAccess: "",
  buyingCenterKnown: undefined,
  budgetConfirmed: undefined,
  painConfirmed: undefined,
  urgencyLevel: undefined,
  competitorKnown: undefined,
  offerSentAt: "",
  lastActivityAt: "",
  nextStep: "",
  notes: "",
  hauffeValueContext: {}
};

function copy(language: "de" | "en") {
  return t(language).dealWinEngine;
}

function booleanValue(value: boolean | undefined): string {
  if (value === true) return "true";
  if (value === false) return "false";
  return "";
}

function parseBoolean(value: string): boolean | undefined {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

function normalizeDecisionMakerAccessValue(value: string | undefined): "yes" | "no" | "unknown" | "" {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return "";
  if (["yes", "ja", "direct", "direkt", "true", "vorhanden", "secured", "available"].includes(normalized)) return "yes";
  if (["no", "nein", "none", "missing", "fehlt", "false", "not_secured", "unavailable"].includes(normalized)) return "no";
  return "unknown";
}

function toggleListValue<T extends string>(items: T[] | undefined, item: T): T[] {
  const current = new Set(items ?? []);
  if (current.has(item)) current.delete(item);
  else current.add(item);
  return Array.from(current);
}

function hasContactDecisionProfile(context: DealWinHauffeValueContext | undefined): context is DealWinHauffeValueContext & { contactDecisionProfile: DealWinContactDecisionProfile } {
  return Boolean(context?.contactDecisionProfile);
}

function formFromDeal(deal: DealWinDeal): DealWinDealInput {
  return {
    title: deal.title,
    companyName: deal.companyName,
    dealValue: deal.dealValue,
    currency: deal.currency,
    salesStage: deal.salesStage,
    expectedCloseDate: deal.expectedCloseDate,
    ownerName: deal.ownerName ?? "",
    status: deal.status,
    contactName: deal.contactName ?? "",
    contactEmail: deal.contactEmail ?? "",
    industry: deal.industry ?? "",
    companySize: deal.companySize ?? "",
    decisionMakerAccess: normalizeDecisionMakerAccessValue(deal.decisionMakerAccess),
    buyingCenterKnown: deal.buyingCenterKnown,
    budgetConfirmed: deal.budgetConfirmed,
    painConfirmed: deal.painConfirmed,
    urgencyLevel: deal.urgencyLevel,
    competitorKnown: deal.competitorKnown,
    offerSentAt: deal.offerSentAt ?? "",
    lastActivityAt: deal.lastActivityAt ?? "",
    nextStep: deal.nextStep ?? "",
    notes: deal.notes ?? "",
    hauffeValueContext: deal.hauffeValueContext ?? {}
  };
}

function cleanForm(input: DealWinDealInput): DealWinDealInput {
  return {
    ...input,
    dealValue: Number(input.dealValue || 0),
    currency: input.currency.trim().toUpperCase(),
    ownerName: input.ownerName?.trim() || undefined,
    contactName: input.contactName?.trim() || undefined,
    contactEmail: input.contactEmail?.trim() || undefined,
    industry: input.industry?.trim() || undefined,
    companySize: input.companySize?.trim() || undefined,
    decisionMakerAccess: normalizeDecisionMakerAccessValue(input.decisionMakerAccess) || undefined,
    offerSentAt: input.offerSentAt || undefined,
    lastActivityAt: input.lastActivityAt || undefined,
    nextStep: input.nextStep?.trim() || undefined,
    notes: input.notes?.trim() || undefined,
    hauffeValueContext: input.hauffeValueContext
  };
}

function riskTone(level: DealWinRiskLevel): "success" | "warning" | "danger" | "default" {
  if (level === "low") return "success";
  if (level === "medium") return "warning";
  if (level === "high" || level === "critical") return "danger";
  return "default";
}

const priorityRank: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3
};

function positiveNumber(value: number | undefined): number {
  const parsed = Number(value ?? 0);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return parsed;
}

function boundedPercentage(value: number): number {
  return Math.min(100, Math.max(0, Math.round(value)));
}

function hasHauffeValueContext(context: DealWinHauffeValueContext | undefined): context is DealWinHauffeValueContext {
  return Boolean(context && Object.keys(context).length > 0);
}

function hasConcreteCustomerValueSegment(context: DealWinHauffeValueContext | undefined): boolean {
  return Boolean(hasHauffeValueContext(context) && !context.modelOnly && (context.segment || context.customerValueScore !== undefined));
}

function contextScoreValue(context: DealWinHauffeValueContext | undefined, keys: Array<keyof DealWinHauffeValueContext>): number | undefined {
  if (!context) return undefined;
  for (const key of keys) {
    const value = context[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return undefined;
}

function contextTextValue(context: DealWinHauffeValueContext | undefined, keys: Array<keyof DealWinHauffeValueContext>): string | undefined {
  if (!context) return undefined;
  for (const key of keys) {
    const value = context[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return undefined;
}

const riskLevelLabels: Record<"de" | "en", Record<string, string>> = {
  de: { low: "Niedrig", medium: "Mittel", high: "Hoch", critical: "Kritisch" },
  en: { low: "Low", medium: "Medium", high: "High", critical: "Critical" }
};

const priorityLabels: Record<"de" | "en", Record<string, string>> = {
  de: { low: "Niedrig", medium: "Mittel", high: "Hoch", critical: "Kritisch" },
  en: { low: "Low", medium: "Medium", high: "High", critical: "Critical" }
};

const confidenceLabels: Record<"de" | "en", Record<string, string>> = {
  de: { low: "Niedrig", medium: "Mittel", high: "Hoch" },
  en: { low: "Low", medium: "Medium", high: "High" }
};

const salesStageLabels: Record<"de" | "en", Record<string, string>> = {
  de: {
    lead: "Lead",
    qualified: "Qualifiziert",
    discovery: "Discovery",
    proposal: "Angebot",
    negotiation: "Verhandlung",
    verbal_commit: "Mündliche Zusage",
    won: "Gewonnen",
    lost: "Verloren"
  },
  en: {
    lead: "Lead",
    qualified: "Qualified",
    discovery: "Discovery",
    proposal: "Proposal",
    negotiation: "Negotiation",
    verbal_commit: "Verbal commit",
    won: "Won",
    lost: "Lost"
  }
};

const dealStatusLabels: Record<"de" | "en", Record<string, string>> = {
  de: { open: "Offen", won: "Gewonnen", lost: "Verloren", disqualified: "Disqualifiziert", archived: "Archiviert" },
  en: { open: "Open", won: "Won", lost: "Lost", disqualified: "Disqualified", archived: "Archived" }
};

const dimensionLabels: Record<"de" | "en", Record<string, string>> = {
  de: {
    customer_fit: "Kundenwert-Passung",
    problem_fit: "Problemfit",
    decision_fit: "Entscheidungsqualität",
    budget_fit: "Budgetklarheit",
    urgency_fit: "Dringlichkeit",
    competition_risk: "Wettbewerbsrisiko",
    process_health: "Prozessqualität",
    commercial_fit: "Wirtschaftliche Deal-Qualität"
  },
  en: {
    customer_fit: "Customer value fit",
    problem_fit: "Problem fit",
    decision_fit: "Decision quality",
    budget_fit: "Budget clarity",
    urgency_fit: "Urgency fit",
    competition_risk: "Competition risk",
    process_health: "Process health",
    commercial_fit: "Commercial deal quality"
  }
};

const scoreReasonLabels: Record<"de" | "en", Record<string, string>> = {
  de: {
    "Company name is known.": "Der Account ist klar zugeordnet und kann gegen ein konkretes Kundenprofil bewertet werden.",
    "The account is clearly identified, so the deal can be reviewed against a concrete customer profile.": "Der Account ist klar zugeordnet und kann gegen ein konkretes Kundenprofil bewertet werden.",
    "Industry context is available.": "Der Branchenkontext zeigt, ob der Kunde grundsätzlich ins Zielmarktbild passt.",
    "Industry context gives Sales a first view on whether the customer fits the target market.": "Der Branchenkontext zeigt, ob der Kunde grundsätzlich ins Zielmarktbild passt.",
    "Company size is available.": "Die Unternehmensgröße hilft, Buying Complexity, Serviceaufwand und wirtschaftliches Potenzial einzuordnen.",
    "Company size supports an initial view on service effort, buying complexity and commercial potential.": "Die Unternehmensgröße hilft, Buying Complexity, Serviceaufwand und wirtschaftliches Potenzial einzuordnen.",
    "Deal value indicates commercial relevance.": "Der Deal hat wirtschaftliche Substanz; jetzt muss klarer werden, ob die Kundenqualität den Aufwand rechtfertigt.",
    "The deal has commercial substance; the next question is whether the customer quality justifies the effort.": "Der Deal hat wirtschaftliche Substanz; jetzt muss klarer werden, ob die Kundenqualität den Aufwand rechtfertigt.",
    "Optional Hauffe Value context is available.": "Kundenwert-Kontext ist für die Deal-Qualität verfügbar.",
    "Customer value context is available for deal quality assessment.": "Kundenwert-Kontext ist für die Deal-Qualität verfügbar.",
    "No Hauffe Value context is attached; score remains independent but confidence is lower.": "Der Kundenwert ist noch nicht belastbar genug belegt; die Entscheidungssicherheit ist dadurch niedriger.",
    "Customer value is not evidenced strongly enough yet; decision confidence is lower.": "Der Kundenwert ist noch nicht belastbar genug belegt; die Entscheidungssicherheit ist dadurch niedriger.",
    "Customer pain is confirmed.": "Das Kundenproblem ist konkret genug, um über geschäftliche Wirkung statt über Produktinteresse zu sprechen.",
    "The customer problem is concrete enough to anchor the conversation in business impact rather than product interest.": "Das Kundenproblem ist konkret genug, um über geschäftliche Wirkung statt über Produktinteresse zu sprechen.",
    "Customer pain is not confirmed.": "Das Kundenproblem ist noch nicht scharf genug; ohne Wirkungsbild bleibt der Deal leicht ein Nice-to-have.",
    "The business problem is not yet sharp enough; without impact clarity, the deal can drift into a nice-to-have discussion.": "Das Kundenproblem ist noch nicht scharf genug; ohne Wirkungsbild bleibt der Deal leicht ein Nice-to-have.",
    "Deal notes provide qualitative context.": "Die Notizen helfen, die echte Kaufsituation jenseits der Pipeline-Felder einzuordnen.",
    "The notes add qualitative context that helps judge the real buying situation beyond pipeline fields.": "Die Notizen helfen, die echte Kaufsituation jenseits der Pipeline-Felder einzuordnen.",
    "A next step exists.": "Der Deal hat Bewegung; der nächste Schritt muss jetzt näher an Budget, Entscheider und Abschlusskriterien führen.",
    "There is movement in the deal; the next step now has to move the customer closer to budget, decision and closing criteria.": "Der Deal hat Bewegung; der nächste Schritt muss jetzt näher an Budget, Entscheider und Abschlusskriterien führen.",
    "Buying center is known.": "Das Buying Center ist sichtbar genug, um Rollen, Einfluss und mögliche Blocker aktiv zu steuern.",
    "The buying center is visible enough to manage roles, influence and possible blockers deliberately.": "Das Buying Center ist sichtbar genug, um Rollen, Einfluss und mögliche Blocker aktiv zu steuern.",
    "Buying center is not known.": "Die Entscheidungsstruktur ist noch intransparent; Sales weiß noch nicht, wer treibt, blockiert oder final entscheidet.",
    "The decision structure is still opaque; Sales does not yet know who drives, blocks or signs off.": "Die Entscheidungsstruktur ist noch intransparent; Sales weiß noch nicht, wer treibt, blockiert oder final entscheidet.",
    "Decision-maker access is direct.": "Direkter Entscheiderzugang gibt dem Team eine echte Chance, Priorität, Budget und Zuschlag zu steuern.",
    "Direct decision-maker access gives the team a real chance to steer priority, budget and final commitment.": "Direkter Entscheiderzugang gibt dem Team eine echte Chance, Priorität, Budget und Zuschlag zu steuern.",
    "Decision-maker access is indirect.": "Der Entscheiderzugang läuft indirekt; der Deal hängt davon ab, wie zuverlässig interne Sponsoren den Case tragen.",
    "Decision-maker access is indirect, so the deal depends on how reliably internal sponsors carry the case.": "Der Entscheiderzugang läuft indirekt; der Deal hängt davon ab, wie zuverlässig interne Sponsoren den Case tragen.",
    "Decision-maker access is missing.": "Die größte Lücke liegt auf der Entscheidungsseite: Ohne Zugang zum wirtschaftlichen oder fachlichen Entscheider bleibt der Deal schwer steuerbar.",
    "The biggest gap is on the decision side: without access to the economic or functional decision maker, the deal is hard to control.": "Die größte Lücke liegt auf der Entscheidungsseite: Ohne Zugang zum wirtschaftlichen oder fachlichen Entscheider bleibt der Deal schwer steuerbar.",
    "Sales stage indicates progressed decision process.": "Die Vertriebsphase zeigt Fortschritt, aber belastbar ist er nur mit explizitem Entscheidungspfad.",
    "The sales stage suggests progress, but that progress is only robust if the decision path is explicit.": "Die Vertriebsphase zeigt Fortschritt, aber belastbar ist er nur mit explizitem Entscheidungspfad.",
    "Budget is confirmed.": "Budgetklarheit macht den Forecast belastbarer, weil Zahlungsbereitschaft nicht mehr nur angenommen wird.",
    "Budget clarity makes the forecast more credible because willingness to pay is no longer just assumed.": "Budgetklarheit macht den Forecast belastbarer, weil Zahlungsbereitschaft nicht mehr nur angenommen wird.",
    "Budget is not confirmed.": "Der Deal-Wert ist attraktiv, aber die Zahlungsbereitschaft ist noch nicht belastbar. Ohne Budgetklärung bleibt die Pipeline-Wahrscheinlichkeit zu optimistisch.",
    "The deal value is attractive, but willingness to pay is not yet proven. Without budget confirmation, the forecast remains exposed.": "Der Deal-Wert ist attraktiv, aber die Zahlungsbereitschaft ist noch nicht belastbar. Ohne Budgetklärung bleibt die Pipeline-Wahrscheinlichkeit zu optimistisch.",
    "Deal value is stated.": "Der erwartete Deal-Wert gibt Sales einen kommerziellen Anker für die Priorisierung.",
    "The expected deal value gives Sales a commercial anchor for prioritization.": "Der erwartete Deal-Wert gibt Sales einen kommerziellen Anker für die Priorisierung.",
    "Late sales stage supports budget confidence.": "Die späte Phase stützt die Budgetannahme, sofern die wirtschaftlichen Bedingungen explizit validiert wurden.",
    "The late stage supports budget confidence, provided the commercial terms have been explicitly validated.": "Die späte Phase stützt die Budgetannahme, sofern die wirtschaftlichen Bedingungen explizit validiert wurden.",
    "Urgency is high.": "Die Dringlichkeit erzeugt Momentum, wenn der Kunde einen klaren Grund hat, jetzt zu handeln.",
    "Urgency is strong enough to create momentum, assuming the customer has a clear reason to act now.": "Die Dringlichkeit erzeugt Momentum, wenn der Kunde einen klaren Grund hat, jetzt zu handeln.",
    "Urgency is medium.": "Es gibt Dringlichkeit, aber Sales muss noch schärfen, warum die Entscheidung jetzt fallen sollte.",
    "There is some urgency, but Sales still needs to sharpen why the decision should happen now.": "Es gibt Dringlichkeit, aber Sales muss noch schärfen, warum die Entscheidung jetzt fallen sollte.",
    "Urgency is low.": "Geringe Dringlichkeit schwächt den Forecast, weil der Status quo die einfachste Entscheidung bleiben kann.",
    "Low urgency weakens the forecast because the status quo can remain the easiest decision.": "Geringe Dringlichkeit schwächt den Forecast, weil der Status quo die einfachste Entscheidung bleiben kann.",
    "Urgency is not specified.": "Die Dringlichkeit ist noch nicht qualifiziert; damit bleibt ein Timing-Risiko offen.",
    "Urgency has not been qualified, so timing risk remains open.": "Die Dringlichkeit ist noch nicht qualifiziert; damit bleibt ein Timing-Risiko offen.",
    "Expected close date is within 90 days.": "Das Abschlussdatum schafft einen brauchbaren Zeitrahmen, um die nächsten Entscheidungsschritte zu steuern.",
    "The close date creates a usable time frame for steering the next decision steps.": "Das Abschlussdatum schafft einen brauchbaren Zeitrahmen, um die nächsten Entscheidungsschritte zu steuern.",
    "Expected close date is in the past.": "Das Abschlussdatum ist überholt; der Deal muss neu qualifiziert werden, bevor er den Forecast stützen darf.",
    "The close date has slipped; the deal needs requalification before it should influence the forecast.": "Das Abschlussdatum ist überholt; der Deal muss neu qualifiziert werden, bevor er den Forecast stützen darf.",
    "Known competition increases deal risk.": "Sichtbarer Wettbewerb erhöht die Anforderungen an Differenzierung, Entscheidungskriterien und internen Sponsor.",
    "Visible competition raises the bar: differentiation, decision criteria and internal sponsorship must be explicit.": "Sichtbarer Wettbewerb erhöht die Anforderungen an Differenzierung, Entscheidungskriterien und internen Sponsor.",
    "No known competitor is recorded.": "Es gibt aktuell keinen sichtbaren Wettbewerber. Das ist positiv, sollte aber nicht mit einem sicheren Abschluss verwechselt werden.",
    "No visible competitor is recorded. That is positive, but the status quo may still be the real opponent.": "Es gibt aktuell keinen sichtbaren Wettbewerber. Das ist positiv, sollte aber nicht mit einem sicheren Abschluss verwechselt werden.",
    "Competition is present in a progressed sales stage.": "Wettbewerb in dieser Phase macht den Deal angreifbar, wenn die Kaufkriterien Hauffe OS nicht klar bevorzugen.",
    "Competition in a progressed stage makes the deal vulnerable unless the buying criteria clearly favor Hauffe OS.": "Wettbewerb in dieser Phase macht den Deal angreifbar, wenn die Kaufkriterien Hauffe OS nicht klar bevorzugen.",
    "A concrete next step exists.": "Der Deal hat Bewegung, weil ein konkreter nächster Schritt definiert ist. Entscheidend ist, ob er Budget, Entscheider und Abschlusskriterien voranbringt.",
    "The deal has movement because a concrete next step is defined; quality depends on whether it advances budget, decision and closing criteria.": "Der Deal hat Bewegung, weil ein konkreter nächster Schritt definiert ist. Entscheidend ist, ob er Budget, Entscheider und Abschlusskriterien voranbringt.",
    "No concrete next step is set.": "Ohne verbindlichen nächsten Schritt kann der Deal trotz Interesse des Kunden ins Stocken geraten.",
    "Without a binding next step, the deal is exposed to drift even if the customer sounds interested.": "Ohne verbindlichen nächsten Schritt kann der Deal trotz Interesse des Kunden ins Stocken geraten.",
    "Recent activity is present.": "Aktuelle Aktivität zeigt, dass die Opportunity noch beweglich ist und aktiv gesteuert werden kann.",
    "Recent activity shows the opportunity is still moving and can be actively steered.": "Aktuelle Aktivität zeigt, dass die Opportunity noch beweglich ist und aktiv gesteuert werden kann.",
    "Last activity is older than 30 days.": "Die letzte relevante Aktivität liegt zu lange zurück; der Deal muss reaktiviert werden, bevor er Forecast-Vertrauen verdient.",
    "The last meaningful activity is too old; the deal needs reactivation before it deserves forecast confidence.": "Die letzte relevante Aktivität liegt zu lange zurück; der Deal muss reaktiviert werden, bevor er Forecast-Vertrauen verdient.",
    "Last activity date is missing.": "Ohne dokumentierte letzte Aktivität lässt sich die Prozessqualität nicht belastbar bewerten.",
    "Without a recorded last activity, process quality cannot be judged reliably.": "Ohne dokumentierte letzte Aktivität lässt sich die Prozessqualität nicht belastbar bewerten.",
    "There has been activity after the offer was sent.": "Nach dem Angebot gab es Bewegung; das spricht dafür, dass der Vorschlag nicht nur versendet, sondern weiter bearbeitet wird.",
    "There was activity after the offer, which suggests the proposal is still being worked rather than just sent and parked.": "Nach dem Angebot gab es Bewegung; das spricht dafür, dass der Vorschlag nicht nur versendet, sondern weiter bearbeitet wird.",
    "Deal value is commercially strong.": "Der wirtschaftliche Hebel ist stark genug, um fokussierte Vertriebsarbeit zu rechtfertigen.",
    "The commercial upside is strong enough to justify focused sales attention.": "Der wirtschaftliche Hebel ist stark genug, um fokussierte Vertriebsarbeit zu rechtfertigen.",
    "Deal value is commercially relevant.": "Der Deal hat wirtschaftliches Gewicht; Qualität und Aufwand entscheiden aber, ob er priorisiert werden sollte.",
    "The deal has relevant commercial weight, but quality and effort still determine whether it should be prioritized.": "Der Deal hat wirtschaftliches Gewicht; Qualität und Aufwand entscheiden aber, ob er priorisiert werden sollte.",
    "Deal value is present but relatively low.": "Der Deal hat einen kommerziellen Anker, sollte aber keinen unverhältnismäßigen Aufwand binden.",
    "The deal has a commercial anchor, but the value is modest and should not absorb disproportionate effort.": "Der Deal hat einen kommerziellen Anker, sollte aber keinen unverhältnismäßigen Aufwand binden.",
    "Currency is defined.": "Die wirtschaftliche Basis ist klar genug, um die Opportunity mit anderen Pipeline-Prioritäten zu vergleichen.",
    "The commercial basis is clear enough to compare the opportunity against other pipeline priorities.": "Die wirtschaftliche Basis ist klar genug, um die Opportunity mit anderen Pipeline-Prioritäten zu vergleichen.",
    "Optional commercial Hauffe Value context is available.": "Wirtschaftlicher Kundenwert-Kontext ist für die Deal-Qualität verfügbar.",
    "Commercial customer value context is available for deal quality assessment.": "Wirtschaftlicher Kundenwert-Kontext ist für die Deal-Qualität verfügbar.",
    "High service effort reduces commercial fit.": "Hoher Serviceaufwand reduziert die wirtschaftliche Deal-Qualität.",
    "High service effort reduces commercial deal quality.": "Hoher Serviceaufwand reduziert die wirtschaftliche Deal-Qualität.",
    "Company size supports commercial interpretation.": "Die Unternehmensgröße hilft, Buying Complexity, Serviceaufwand und Account-Potenzial wirtschaftlich einzuordnen.",
    "Company size helps interpret buying complexity, service effort and account potential.": "Die Unternehmensgröße hilft, Buying Complexity, Serviceaufwand und Account-Potenzial wirtschaftlich einzuordnen."
  },
  en: {
    "Optional Hauffe Value context is available.": "Customer value context is available for deal quality assessment.",
    "No Hauffe Value context is attached; score remains independent but confidence is lower.": "Customer value is not evidenced strongly enough yet; decision confidence is lower.",
    "Optional commercial Hauffe Value context is available.": "Commercial customer value context is available for deal quality assessment.",
    "High service effort reduces commercial fit.": "High service effort reduces commercial deal quality."
  }
};

const riskLabels: Record<"de" | "en", Record<string, { title: string; description: string; action: string }>> = {
  de: {
    no_decision_maker_access: {
      title: "Entscheiderzugang nicht abgesichert",
      description: "Solange die Person mit Einfluss auf Budget, Priorität oder finalen Zuschlag nicht direkt erreichbar ist, bleibt der Deal schwer steuerbar.",
      action: "Nächsten Schritt mit der Person sichern, die Budget, Priorität oder Zuschlag beeinflusst."
    },
    decision_maker_access_missing: {
      title: "Entscheiderzugang nicht abgesichert",
      description: "Solange die Person mit Einfluss auf Budget, Priorität oder finalen Zuschlag nicht direkt erreichbar ist, bleibt der Deal schwer steuerbar.",
      action: "Nächsten Schritt mit der Person sichern, die Budget, Priorität oder Zuschlag beeinflusst."
    },
    buying_center_unknown: {
      title: "Buying Center nicht sichtbar genug",
      description: "Die Entscheidungsstruktur ist noch nicht belastbar. Solange unklar bleibt, wer treibt, blockiert und final entscheidet, bleibt der Deal schwer steuerbar.",
      action: "Buying Center sichtbar machen: Treiber, Blocker, Entscheider und interne Sponsoren sauber abbilden."
    },
    budget_not_confirmed: {
      title: "Zahlungsbereitschaft nicht belegt",
      description: "Der Deal-Wert ist attraktiv, aber Budgetlogik und Zahlungsbereitschaft sind noch nicht belastbar genug für einen sicheren Forecast.",
      action: "Budget aktiv qualifizieren: Zahlungsbereitschaft, Entscheidungslogik und wirtschaftlichen Nutzen festziehen."
    },
    pain_not_confirmed: {
      title: "Business Pain noch nicht scharf genug",
      description: "Das Kundenproblem ist noch nicht klar genug mit geschäftlicher Wirkung, Priorität und Kosten des Nicht-Handelns verbunden.",
      action: "Problem, Wirkung und Folgen des Nicht-Handelns konkretisieren."
    },
    next_step_missing: {
      title: "Nächster Schritt nicht verbindlich",
      description: "Ohne konkreten nächsten Schritt kann der Deal trotz Interesse des Kunden ins Stocken geraten.",
      action: "Verbindlichen nächsten Schritt vereinbaren, der Budget, Entscheidungskriterien oder finalen Zuschlag voranbringt."
    },
    missing_next_step: {
      title: "Nächster Schritt nicht verbindlich",
      description: "Ohne konkreten nächsten Schritt kann der Deal trotz Interesse des Kunden ins Stocken geraten.",
      action: "Verbindlichen nächsten Schritt vereinbaren, der Budget, Entscheidungskriterien oder finalen Zuschlag voranbringt."
    },
    activity_stale: {
      title: "Deal-Momentum ist abgekühlt",
      description: "Die letzte relevante Aktivität liegt zu lange zurück, um den Deal als aktiv gesteuert zu bewerten.",
      action: "Deal mit konkretem Kundencommitment und datiertem nächsten Schritt reaktivieren."
    },
    stale_activity: {
      title: "Deal-Momentum ist abgekühlt",
      description: "Die letzte relevante Aktivität liegt zu lange zurück, um den Deal als aktiv gesteuert zu bewerten.",
      action: "Deal mit konkretem Kundencommitment und datiertem nächsten Schritt reaktivieren."
    },
    activity_missing: {
      title: "Prozessqualität schwer bewertbar",
      description: "Ohne dokumentierte Kundeninteraktion lässt sich der Vertriebsprozess nicht belastbar einschätzen.",
      action: "Letzte relevante Kundeninteraktion erfassen und nächsten Entscheidungsschritt festlegen."
    },
    close_date_in_past: {
      title: "Forecast-Datum ist überholt",
      description: "Das erwartete Abschlussdatum ist bereits überschritten; der Deal braucht eine neue Qualifizierung.",
      action: "Abschlussdatum, Kaufprozess und aktiven Deal-Status neu validieren."
    },
    expected_close_date_past: {
      title: "Abschlussdatum liegt in der Vergangenheit",
      description: "Das erwartete Abschlussdatum ist bereits überschritten.",
      action: "Abschlussdatum prüfen oder Deal-Status neu qualifizieren."
    },
    competition_known: {
      title: "Wettbewerbsdruck sichtbar",
      description: "Ein Wettbewerber oder eine Alternative ist aktiv; der Deal braucht klare Differenzierung und explizite Kaufkriterien.",
      action: "Differenzierung, Entscheidungskriterien und den Grund für Hauffe OS schärfen."
    },
    competitor_known: {
      title: "Wettbewerbsdruck sichtbar",
      description: "Ein Wettbewerber oder eine Alternative ist aktiv; der Deal braucht klare Differenzierung und explizite Kaufkriterien.",
      action: "Differenzierung, Entscheidungskriterien und den Grund für Hauffe OS schärfen."
    },
    deal_value_missing: {
      title: "Wirtschaftlicher Wert nicht qualifiziert",
      description: "Ohne belastbaren Deal-Wert lässt sich die Opportunity nicht sinnvoll priorisieren.",
      action: "Erwarteten Deal-Wert und Vertriebsfokus klären."
    },
    deal_value_low: {
      title: "Deal-Wert sehr niedrig",
      description: "Der Deal-Wert rechtfertigt möglicherweise keinen hohen Vertriebsaufwand.",
      action: "Prüfen, ob der Deal standardisiert oder disqualifiziert werden sollte."
    },
    offer_sent_without_followup: {
      title: "Angebot wird nicht aktiv bearbeitet",
      description: "Das Angebot ist versendet, aber es gibt keine spätere Aktivität, die Kundenbewegung zeigt.",
      action: "Angebot nachfassen und konkreten nächsten Entscheidungsschritt sichern."
    },
    early_stage_near_close: {
      title: "Forecast-Timing noch nicht glaubwürdig",
      description: "Der Deal ist noch früh, obwohl das erwartete Abschlussdatum nahe ist. Die Timing-Annahme ist dadurch fragil.",
      action: "Kaufprozess, Entscheidungspfad und Abschlussdatum mit dem Kunden validieren."
    },
    hauffe_value_context_missing: {
      title: "Kundenwert noch nicht belastbar belegt",
      description: "Der Deal ist wirtschaftlich relevant, aber der Kundenwert ist noch nicht belastbar genug belegt.",
      action: "Kundenwert-Score, ideale Kundenpassung und wirtschaftliche Deal-Qualität vor der nächsten Entscheidung klären."
    },
    missing_hauffe_value_context: {
      title: "Kundenwert noch nicht belastbar belegt",
      description: "Der Deal ist wirtschaftlich relevant, aber der Kundenwert ist noch nicht belastbar genug belegt.",
      action: "Kundenwert-Score, ideale Kundenpassung und wirtschaftliche Deal-Qualität vor der nächsten Entscheidung klären."
    },
    customer_value_fit_weak: {
      title: "Kundenwert-Passung kritisch",
      description: "Es ist noch unklar, ob dieser Kunde wirklich zum idealen Kundenprofil passt.",
      action: "Kundenwert-Segment, Entscheidungsqualität und erforderlichen Vertriebsaufwand prüfen."
    },
    customer_value_commercial_risk: {
      title: "Wirtschaftliche Deal-Qualität kritisch",
      description: "Der Kundenwert deutet auf schwache Profitabilität oder hohen operativen Aufwand hin.",
      action: "Marge, Serviceaufwand und wirtschaftliche Deal-Qualität prüfen, bevor weitere Vertriebsressourcen gebunden werden."
    },
    contact_profile_business_case_needed: {
      title: "Entscheidungsgrundlage muss belastbarer werden",
      description: "Das Ansprechpartner-Profil deutet darauf hin, dass Business Case, ROI-Logik oder Zahlenbasis für die nächste Entscheidung wichtiger werden.",
      action: "Den nächsten Schritt mit Business Case, ROI-Logik und klaren Entscheidungskriterien führen."
    },
    contact_profile_risk_assurance_needed: {
      title: "Entscheidungsrisiko aktiv reduzieren",
      description: "Das Ansprechpartner-Profil deutet darauf hin, dass Referenz, Umsetzungsplan oder Nachweis den Deal sicherer machen.",
      action: "Wahrgenommenes Risiko mit Referenz, Umsetzungsplan oder Pilot reduzieren."
    },
    contact_profile_consensus_path_needed: {
      title: "Buying Center und Entscheidungspfad absichern",
      description: "Das Ansprechpartner-Profil deutet darauf hin, dass interne Rollen, Sponsor und nächster Beschluss noch nicht belastbar genug sind.",
      action: "Buying Center, internen Sponsor und nächsten Entscheidungsschritt klären."
    },
    contact_profile_relationship_sponsor_needed: {
      title: "Vertrauen und Sponsor bewusst aufbauen",
      description: "Das Ansprechpartner-Profil deutet darauf hin, dass Vertrauen und interne Unterstützung wichtiger sind als weitere Produktargumente.",
      action: "Sponsor-Gespräch sichern und den nächsten gemeinsamen Commitment-Schritt festlegen."
    }
  },
  en: {
    no_decision_maker_access: {
      title: "Decision-maker access is not secured",
      description: "The deal is difficult to control while the person influencing budget, priority or final approval is not directly reachable.",
      action: "Secure a next step with the person who can influence budget, priority or final approval."
    },
    decision_maker_access_missing: {
      title: "Decision-maker access is not secured",
      description: "The deal is difficult to control while the person influencing budget, priority or final approval is not directly reachable.",
      action: "Secure a next step with the person who can influence budget, priority or final approval."
    },
    buying_center_unknown: {
      title: "Buying center is not visible enough",
      description: "Sales does not yet know who drives the need, who blocks the case, who decides and who must be convinced internally.",
      action: "Map drivers, blockers, decision makers and internal sponsors."
    },
    budget_not_confirmed: {
      title: "Willingness to pay is not proven",
      description: "The deal value is attractive, but budget logic and willingness to pay are not yet robust enough for a confident forecast.",
      action: "Actively qualify budget: align willingness to pay, decision logic and economic value."
    },
    pain_not_confirmed: {
      title: "Business pain is not sharp enough",
      description: "The customer problem is not yet clearly tied to business impact, priority and cost of inaction.",
      action: "Clarify the business problem, impact and consequence of doing nothing."
    },
    next_step_missing: {
      title: "No binding next step",
      description: "The deal can drift because there is no concrete next action that moves the customer closer to a decision.",
      action: "Agree a next step tied to budget, decision criteria or final approval."
    },
    missing_next_step: {
      title: "No binding next step",
      description: "The deal can drift because there is no concrete next action that moves the customer closer to a decision.",
      action: "Agree a next step tied to budget, decision criteria or final approval."
    },
    activity_stale: {
      title: "Deal momentum has cooled down",
      description: "The last meaningful activity is too old to treat the opportunity as actively controlled.",
      action: "Reactivate the deal with a concrete customer commitment and dated next step."
    },
    stale_activity: {
      title: "Deal momentum has cooled down",
      description: "The last meaningful activity is too old to treat the opportunity as actively controlled.",
      action: "Reactivate the deal with a concrete customer commitment and dated next step."
    },
    activity_missing: {
      title: "Process quality is hard to judge",
      description: "Without a recorded customer interaction, the sales process cannot be assessed reliably.",
      action: "Record the last meaningful customer interaction and define the next decision step."
    },
    close_date_in_past: {
      title: "Forecast date has slipped",
      description: "The expected close date has already passed, so the deal needs requalification before it remains in the forecast.",
      action: "Requalify close date, buying process and active deal status."
    },
    expected_close_date_past: {
      title: "Expected close date is in the past",
      description: "The expected close date has already passed.",
      action: "Validate the close date or qualify whether the deal is still active."
    },
    competition_known: {
      title: "Competitive pressure is visible",
      description: "A competitor or alternative is active, so the deal needs sharper differentiation and explicit decision criteria.",
      action: "Sharpen differentiation, decision criteria and the reason Hauffe OS should win."
    },
    competitor_known: {
      title: "Competitive pressure is visible",
      description: "A competitor or alternative is active, so the deal needs sharper differentiation and explicit decision criteria.",
      action: "Sharpen differentiation, decision criteria and the reason Hauffe OS should win."
    },
    deal_value_missing: {
      title: "Commercial value is not qualified",
      description: "The opportunity cannot be prioritized properly without a credible expected deal value.",
      action: "Clarify expected deal value and whether the opportunity deserves sales focus."
    },
    deal_value_low: {
      title: "Deal value is very low",
      description: "The deal value may not justify extensive sales effort.",
      action: "Check whether the deal should be standardized or disqualified."
    },
    offer_sent_without_followup: {
      title: "Offer is not being actively worked",
      description: "An offer has been sent, but there is no later activity showing that the customer is still moving.",
      action: "Follow up on the offer and secure a concrete next decision step."
    },
    early_stage_near_close: {
      title: "Forecast timing is not credible yet",
      description: "The deal is still early while the expected close date is near, which makes the timing assumption fragile.",
      action: "Validate buying process, decision path and close date with the customer."
    },
    hauffe_value_context_missing: {
      title: "Customer value is not evidenced enough",
      description: "The deal is commercially relevant, but customer value is not evidenced strongly enough yet.",
      action: "Clarify customer value score, ideal customer profile fit and commercial deal quality before the next decision."
    },
    missing_hauffe_value_context: {
      title: "Customer value is not evidenced enough",
      description: "The deal is commercially relevant, but customer value is not evidenced strongly enough yet.",
      action: "Clarify customer value score, ideal customer profile fit and commercial deal quality before the next decision."
    },
    customer_value_fit_weak: {
      title: "Customer value fit is critical",
      description: "It is still unclear whether this customer truly matches the ideal customer profile.",
      action: "Review customer value segment, decision quality and required sales effort before committing more resources."
    },
    customer_value_commercial_risk: {
      title: "Commercial deal quality is critical",
      description: "Customer value indicates weak profitability or high operational effort.",
      action: "Check margin, service effort and commercial deal quality before advancing the deal."
    },
    contact_profile_business_case_needed: {
      title: "Decision basis needs stronger evidence",
      description: "The contact profile suggests that business case, ROI logic or numbers-based proof matter more for the next decision.",
      action: "Lead the next step with business case, ROI logic and clear decision criteria."
    },
    contact_profile_risk_assurance_needed: {
      title: "Decision risk needs active reduction",
      description: "The contact profile suggests that reference, implementation plan or proof point can make the deal safer.",
      action: "Reduce perceived risk with reference, implementation path or pilot."
    },
    contact_profile_consensus_path_needed: {
      title: "Buying center and decision path need clarity",
      description: "The contact profile suggests that internal roles, sponsor and next decision need to become visible.",
      action: "Clarify buying center, internal sponsor and next decision step."
    },
    contact_profile_relationship_sponsor_needed: {
      title: "Trust and sponsorship need deliberate attention",
      description: "The contact profile suggests that trust and internal support matter more than more product arguments.",
      action: "Secure a sponsor conversation and align on the next mutual commitment."
    }
  }
};

const actionLabels: Record<"de" | "en", Record<string, { title: string; description: string }>> = {
  de: {
    decision_maker_access_missing: {
      title: "Entscheiderzugang sichern",
      description: "Den nächsten Schritt in echte Entscheidungsarbeit übersetzen: Person mit Einfluss auf Budget, Priorität oder Zuschlag identifizieren und direkten Zugang herstellen."
    },
    buying_center_unknown: {
      title: "Buying Center sichtbar machen",
      description: "Sales gewinnt Klarheit über Treiber, Blocker, Entscheider und Sponsoren. Dadurch wird der nächste Schritt nicht nur Aktivität, sondern steuerbare Entscheidungsarbeit."
    },
    budget_not_confirmed: {
      title: "Budgetrahmen belastbar klären",
      description: "Nicht allgemein nach Budget fragen. Sales sollte Zahlungsbereitschaft, Entscheidungslogik und wirtschaftlichen Nutzen so verbinden, dass der nächste Schritt budgetfähig wird."
    },
    pain_not_confirmed: {
      title: "Business Pain entscheidungsfähig machen",
      description: "Problem, messbare Wirkung und Kosten des Nicht-Handelns so schärfen, dass der Deal als geschäftliche Entscheidung geführt werden kann."
    },
    next_step_missing: {
      title: "Verbindlichen Entscheidungsschritt schaffen",
      description: "Einen datierten nächsten Schritt vereinbaren, der Budget, Entscheidungskriterien oder finalen Zuschlag konkret voranbringt und den Forecast weniger angreifbar macht."
    },
    activity_stale: {
      title: "Deal mit Commitment reaktivieren",
      description: "Momentum über ein konkretes Kundencommitment und einen datierten nächsten Entscheidungsschritt neu aufbauen."
    },
    activity_missing: {
      title: "Prozessnachweis herstellen",
      description: "Letzte relevante Kundeninteraktion und nächsten Entscheidungsschritt dokumentieren, damit die Prozessqualität belastbar bewertbar wird."
    },
    close_date_in_past: {
      title: "Forecast-Timing neu qualifizieren",
      description: "Kaufprozess, Abschlussdatum und aktiven Deal-Status neu bestätigen, bevor die Opportunity weiter im Forecast bleibt."
    },
    competition_known: {
      title: "Wettbewerbsdifferenzierung schärfen",
      description: "Entscheidungskriterien explizit machen und mit dem Hauffe-OS-Nutzen verbinden, damit Alternative und Status quo angreifbarer werden."
    },
    deal_value_missing: {
      title: "Wirtschaftlichen Wert klären",
      description: "Erwarteten Deal-Wert und Vertriebsaufwand quantifizieren, damit Fokus und Priorität des Deals belastbar entschieden werden können."
    },
    deal_value_low: {
      title: "Passende Sales Motion festlegen",
      description: "Entscheiden, ob der Deal standardisiert, niedriger priorisiert oder disqualifiziert werden sollte, damit Vertriebsaufwand und Wert zusammenpassen."
    },
    offer_sent_without_followup: {
      title: "Angebot in Entscheidungsschritt übersetzen",
      description: "Das Angebot mit einem konkreten Entscheidungsmeilenstein nachfassen, damit es nicht bei passivem Dokumentenaustausch bleibt."
    },
    early_stage_near_close: {
      title: "Timing und Kaufprozess validieren",
      description: "Kaufprozess, Entscheidungspfad und Abschlussdatum mit dem Kunden so klären, dass die Timing-Annahme tragfähig wird."
    },
    hauffe_value_context_missing: {
      title: "Kundenwert belastbar machen",
      description: "Kundenwert-Score, ideale Kundenpassung und wirtschaftliche Deal-Qualität klären, damit Priorisierung und Deal-Qualität belastbarer werden."
    },
    customer_value_fit_weak: {
      title: "Kundenwert-Passung prüfen",
      description: "Kundenwert-Segment, Entscheidungsqualität und erforderlichen Vertriebsaufwand prüfen, bevor weitere Ressourcen in den Deal fließen."
    },
    customer_value_commercial_risk: {
      title: "Wirtschaftliche Deal-Qualität prüfen",
      description: "Profitabilität, Marge und Serviceaufwand prüfen, damit Umsatzqualität nicht mit Deal-Größe verwechselt wird."
    },
    contact_profile_business_case_needed: {
      title: "Business Case entscheidungsfähig machen",
      description: "Nutzen, Aufwand und wirtschaftlichen Effekt in eine klare Entscheidungsgrundlage übersetzen. Dadurch bekommt der Ansprechpartner Material, das intern tragfähig ist."
    },
    contact_profile_risk_assurance_needed: {
      title: "Entscheidungsrisiko reduzieren",
      description: "Referenz, Umsetzungsplan oder Nachweis so einsetzen, dass der nächste Schritt für einen risikosensiblen Ansprechpartner sicher genug wird."
    },
    contact_profile_consensus_path_needed: {
      title: "Stakeholder-Pfad ausrichten",
      description: "Sponsor, Rollen und nächsten Beschluss klären. Dadurch wird das Buying Center zu einem steuerbaren Entscheidungspfad statt zu einer unbekannten Gruppe."
    },
    contact_profile_relationship_sponsor_needed: {
      title: "Sponsorship bewusst aufbauen",
      description: "Sponsor-Gespräch und gemeinsames Commitment schaffen, damit Vertrauen zum Entscheidungsfaktor wird und nicht nur Beziehungssignal bleibt."
    }
  },
  en: {
    decision_maker_access_missing: {
      title: "Secure decision-maker access",
      description: "Turn the next step into real decision work: identify who can influence budget, priority or final approval and secure direct access."
    },
    buying_center_unknown: {
      title: "Make the buying center visible",
      description: "Sales gains clarity on drivers, blockers, decision makers and sponsors. The next step becomes controllable decision work, not just activity."
    },
    budget_not_confirmed: {
      title: "Clarify budget logic",
      description: "Do not ask for budget in general terms. Connect willingness to pay, decision logic and business value so the next step becomes budget-ready."
    },
    pain_not_confirmed: {
      title: "Make business pain decision-ready",
      description: "Sharpen problem, measurable impact and cost of inaction so the deal can be led as a business decision."
    },
    next_step_missing: {
      title: "Create a binding decision step",
      description: "Agree a dated next step that advances budget, decision criteria or final approval and makes the forecast less exposed."
    },
    activity_stale: {
      title: "Reactivate with commitment",
      description: "Rebuild momentum through a concrete customer commitment and a dated next decision step."
    },
    activity_missing: {
      title: "Create process evidence",
      description: "Document the last meaningful customer interaction and next decision step so process quality becomes assessable."
    },
    close_date_in_past: {
      title: "Requalify forecast timing",
      description: "Reconfirm buying process, close date and active deal status before this opportunity stays in the forecast."
    },
    competition_known: {
      title: "Sharpen competitive differentiation",
      description: "Make decision criteria explicit and connect them to the Hauffe OS value case so alternatives and status quo become easier to challenge."
    },
    deal_value_missing: {
      title: "Clarify commercial value",
      description: "Quantify expected deal value and sales effort so focus and priority can be decided reliably."
    },
    deal_value_low: {
      title: "Choose the right sales motion",
      description: "Decide whether the deal should be standardized, deprioritized or disqualified so effort and upside stay aligned."
    },
    offer_sent_without_followup: {
      title: "Turn the offer into a decision step",
      description: "Follow up the proposal with a concrete decision milestone so it does not remain passive document exchange."
    },
    early_stage_near_close: {
      title: "Validate timing and buying process",
      description: "Align buying process, decision path and close date with the customer so the timing assumption becomes credible."
    },
    hauffe_value_context_missing: {
      title: "Evidence customer value",
      description: "Clarify customer value score, ideal customer fit and commercial deal quality so prioritization and deal quality become more reliable."
    },
    customer_value_fit_weak: {
      title: "Review customer value fit",
      description: "Use customer value segment, decision quality and required sales effort before committing more resources to the deal."
    },
    customer_value_commercial_risk: {
      title: "Review commercial deal quality",
      description: "Check profitability, margin and service effort so revenue quality does not get confused with deal size."
    },
    contact_profile_business_case_needed: {
      title: "Make the business case decision-ready",
      description: "Translate value, effort and economic effect into a clear decision basis so the contact can carry the case internally."
    },
    contact_profile_risk_assurance_needed: {
      title: "Reduce decision risk",
      description: "Use reference, implementation path or proof point to make the next decision feel safe enough for a risk-sensitive contact."
    },
    contact_profile_consensus_path_needed: {
      title: "Align the stakeholder path",
      description: "Clarify sponsor, roles and next decision step so the buying center becomes a controllable path rather than an unknown group."
    },
    contact_profile_relationship_sponsor_needed: {
      title: "Build sponsorship deliberately",
      description: "Create a sponsor conversation and mutual commitment so trust becomes a decision asset, not only a relationship signal."
    }
  }
};

const leverLabels: Record<"de" | "en", Record<string, { title: string; action: string }>> = {
  de: {
    confirm_budget: { title: "Budget aktiv qualifizieren", action: "Nicht allgemein nach Budget fragen, sondern Zahlungsbereitschaft, Entscheidungslogik und wirtschaftlichen Nutzen gemeinsam festziehen." },
    secure_decision_maker_access: { title: "Entscheiderzugang herstellen", action: "Der nächste Schritt muss ein Gespräch mit der Person sichern, die Budget, Priorität oder finalen Zuschlag beeinflusst." },
    clarify_buying_center: { title: "Buying Center sichtbar machen", action: "Wer treibt den Bedarf, wer blockiert, wer entscheidet und wer muss intern überzeugt werden?" },
    clarify_customer_fit: { title: "Kundenwert-Passung klären", action: "Prüfen, ob der Deal zum idealen Kundenprofil und zur gewünschten Deal-Qualität passt." },
    schedule_next_step: { title: "Verbindlichen nächsten Schritt schaffen", action: "Nächsten Schritt so setzen, dass Budget, Entscheidungskriterien oder finaler Zuschlag konkret vorankommen." },
    set_next_step: { title: "Verbindlichen nächsten Schritt schaffen", action: "Nächsten Schritt so setzen, dass Budget, Entscheidungskriterien oder finaler Zuschlag konkret vorankommen." },
    validate_close_date: { title: "Forecast-Timing neu qualifizieren", action: "Abschlussdatum, Kaufprozess und Entscheidungspfad mit dem Kunden validieren." },
    sharpen_competitive_differentiation: { title: "Wettbewerbsdifferenzierung schärfen", action: "Entscheidungskriterien so klären, dass der Wert von Hauffe OS gegenüber Alternative und Status quo sichtbar wird." },
    confirm_customer_pain: { title: "Business Pain schärfen", action: "Problem, messbare Wirkung und Kosten des Nicht-Handelns klären." },
    clarify_hauffe_value_context: { title: "Kundenwert belastbar machen", action: "Kundenwert-Score, ideale Kundenpassung und Entscheidungsqualität für den Deal klären." },
    review_customer_value_fit: { title: "Kundenwert-Passung prüfen", action: "Kundenwert-Segment, Passung zum idealen Kundenprofil und erforderlichen Vertriebsaufwand prüfen." },
    review_commercial_value_fit: { title: "Wirtschaftliche Deal-Qualität prüfen", action: "Profitabilität, Marge und Serviceaufwand prüfen." },
    contact_profile_business_case: { title: "Business Case entscheidungsfähig machen", action: "Kurzen Business Case mit ROI-Logik, Budgetwirkung und Entscheidungskriterien für das nächste Gespräch vorbereiten." },
    contact_profile_reduce_risk: { title: "Entscheidungsrisiko reduzieren", action: "Referenz, Umsetzungsplan oder Pilot nutzen, damit der nächste Schritt sicher genug wirkt." },
    contact_profile_align_buying_center: { title: "Stakeholder-Pfad ausrichten", action: "Klären, wer den Bedarf intern trägt und welcher Beschluss als Nächstes nötig ist." }
  },
  en: {
    confirm_budget: { title: "Qualify budget actively", action: "Do not ask for budget in general terms: align willingness to pay, decision logic and economic value." },
    secure_decision_maker_access: { title: "Secure decision-maker access", action: "The next step must secure a conversation with the person who influences budget, priority or final approval." },
    clarify_buying_center: { title: "Make the buying center visible", action: "Identify who drives the need, who blocks, who decides and who must be convinced internally." },
    clarify_customer_fit: { title: "Clarify customer value fit", action: "Check whether the deal matches the ideal customer profile and desired deal quality." },
    schedule_next_step: { title: "Create a binding next step", action: "Set a next step that advances budget, decision criteria or final approval." },
    set_next_step: { title: "Create a binding next step", action: "Set a next step that advances budget, decision criteria or final approval." },
    validate_close_date: { title: "Requalify forecast timing", action: "Validate close date, buying process and decision path with the customer." },
    sharpen_competitive_differentiation: { title: "Sharpen competitive differentiation", action: "Clarify decision criteria so the value of Hauffe OS is visible against alternatives and the status quo." },
    confirm_customer_pain: { title: "Sharpen business pain", action: "Clarify the problem, measurable impact and cost of inaction." },
    clarify_hauffe_value_context: { title: "Evidence customer value", action: "Clarify customer value score, ideal customer fit and decision confidence for this deal." },
    review_customer_value_fit: { title: "Review customer value fit", action: "Review customer value segment, ideal customer profile fit and required sales effort." },
    review_commercial_value_fit: { title: "Review commercial deal quality", action: "Check profitability, margin and service effort." },
    contact_profile_business_case: { title: "Make the business case decision-ready", action: "Prepare a short business case with ROI logic, budget impact and decision criteria for the next conversation." },
    contact_profile_reduce_risk: { title: "Reduce decision risk", action: "Use reference, implementation path or pilot to make the next step feel safe enough." },
    contact_profile_align_buying_center: { title: "Align stakeholder path", action: "Clarify who carries the need internally and which decision is required next." }
  }
};

export function labelFor(labels: Record<"de" | "en", Record<string, string>>, language: "de" | "en", value: string): string {
  return labels[language][value] ?? value;
}

function translateReason(reason: string, language: "de" | "en"): string {
  return scoreReasonLabels[language][reason] ?? reason;
}

export function getDimensionLabel(key: string | undefined, fallback: string | undefined, language: "de" | "en"): string {
  if (key && dimensionLabels[language][key]) return dimensionLabels[language][key];
  return fallback ?? key ?? "-";
}

export function getRiskView(risk: DealWinAssessment["riskReasons"][number], language: "de" | "en") {
  const mapped = risk.riskKey ? riskLabels[language][risk.riskKey] : undefined;
  return {
    title: mapped?.title ?? risk.riskTitle ?? risk.riskKey ?? "-",
    description: mapped?.description ?? risk.riskDescription ?? "",
    action: mapped?.action ?? risk.suggestedAction
  };
}

export function getLeverView(lever: DealWinImprovementLever, language: "de" | "en") {
  const mapped = leverLabels[language][lever.leverKey];
  return {
    title: mapped?.title ?? lever.title,
    action: mapped?.action ?? lever.suggestedAction
  };
}

export function getRecommendedActionView(action: DealWinAssessment["recommendedActions"][number], language: "de" | "en") {
  const riskKey = action.relatedRiskKey ?? action.actionKey?.replace(/^action_/, "");
  const mappedAction = riskKey ? actionLabels[language][riskKey] : undefined;
  return {
    title: mappedAction?.title ?? action.title,
    description: mappedAction?.description ?? action.description
  };
}

function getAssessmentVersionLabel(version: string, language: "de" | "en"): string {
  if (version === "deal-win-engine-v1") return language === "en" ? "v1" : "Version 1";
  return version;
}

export function getAssessmentInterpretation(winProbability: number, language: "de" | "en"): string {
  if (language === "en") {
    if (winProbability < 25) return "Very low win probability. This deal should be reviewed critically or disqualified.";
    if (winProbability < 50) return "Low win probability. Important prerequisites are still missing.";
    if (winProbability < 70) return "Medium win probability. The right levers can improve this deal significantly.";
    if (winProbability < 85) return "High win probability. Focus on securing the deal and the next steps.";
    return "Very high win probability. Secure the close with consistent follow-through.";
  }
  if (winProbability < 25) return "Sehr geringe Abschlusswahrscheinlichkeit. Der Deal sollte kritisch geprüft oder disqualifiziert werden.";
  if (winProbability < 50) return "Geringe Abschlusswahrscheinlichkeit. Es fehlen wesentliche Voraussetzungen.";
  if (winProbability < 70) return "Mittlere Abschlusswahrscheinlichkeit. Mit den richtigen Hebeln kann der Deal deutlich verbessert werden.";
  if (winProbability < 85) return "Hohe Abschlusswahrscheinlichkeit. Fokus auf Absicherung und nächste Schritte.";
  return "Sehr hohe Abschlusswahrscheinlichkeit. Abschluss konsequent absichern.";
}

function getContactProfileReviewHint(context: DealWinHauffeValueContext | undefined, language: "de" | "en"): string | undefined {
  const profile = context?.contactDecisionProfile;
  if (!profile) return undefined;
  const analytical = profile.decisionStyle === "analytical" || profile.communicationPreference === "numbers_and_facts" || profile.proofPreference.includes("business_case") || profile.proofPreference.includes("roi_calculation");
  const riskAverse = profile.decisionStyle === "risk_averse" || profile.proofPreference.includes("customer_reference") || profile.proofPreference.includes("implementation_plan") || profile.recommendedApproach.includes("reduce_perceived_risk");
  const consensus = profile.decisionStyle === "consensus_driven" || profile.recommendedApproach.includes("secure_internal_sponsor") || profile.recommendedApproach.includes("clarify_decision_path");
  if (language === "en") {
    if (analytical) return "For this contact, the next step should strengthen the business case, ROI logic and decision basis.";
    if (riskAverse) return "For this contact, Sales should reduce perceived risk with proof, reference or a clear implementation path.";
    if (consensus) return "For this contact, Sales should clarify the buying center, internal sponsor and next decision path.";
    return "The contact profile indicates that the next step should be led through decision confidence, proof and clear commitment.";
  }
  if (analytical) return "Für diesen Ansprechpartner zählt eine belastbare Entscheidungsgrundlage: Business Case, ROI-Logik und klare Entscheidungskriterien sollten den nächsten Schritt führen.";
  if (riskAverse) return "Für diesen Ansprechpartner sollte Sales wahrgenommenes Risiko reduzieren: mit Referenz, Umsetzungsplan oder belastbarem Nachweis.";
  if (consensus) return "Für diesen Ansprechpartner sollte Sales Buying Center, internen Sponsor und Entscheidungspfad sichtbar machen.";
  return "Das Profil deutet darauf hin, dass der Deal stärker über Sicherheit, Nachweis und Entscheidungsfähigkeit geführt werden sollte.";
}

export function sortImprovementLevers(levers: DealWinImprovementLever[]): DealWinImprovementLever[] {
  return levers.slice().sort((left, right) => {
    const priorityDifference = (priorityRank[left.priority] ?? 9) - (priorityRank[right.priority] ?? 9);
    if (priorityDifference !== 0) return priorityDifference;
    const rightImpact = positiveNumber(right.estimatedScoreImpact) + positiveNumber(right.estimatedWinProbabilityImpact);
    const leftImpact = positiveNumber(left.estimatedScoreImpact) + positiveNumber(left.estimatedWinProbabilityImpact);
    return rightImpact - leftImpact;
  });
}

export function calculateWhatIfSimulation(assessment: DealWinAssessment, selectedLeverKeys: string[]) {
  const selected = assessment.improvementLevers.filter((lever) => selectedLeverKeys.includes(lever.leverKey));
  const scoreImpact = selected.reduce((total, lever) => total + positiveNumber(lever.estimatedScoreImpact), 0);
  const probabilityImpact = selected.reduce((total, lever) => total + positiveNumber(lever.estimatedWinProbabilityImpact), 0);
  return {
    selected,
    scoreImpact,
    probabilityImpact,
    simulatedScore: boundedPercentage(positiveNumber(assessment.scoreTotal) + scoreImpact),
    simulatedWinProbability: boundedPercentage(positiveNumber(assessment.winProbability) + probabilityImpact)
  };
}

export function getNextBestAction(assessment: DealWinAssessment, language: "de" | "en"): string {
  const topLever = sortImprovementLevers(assessment.improvementLevers)[0];
  if (topLever?.suggestedAction) return getLeverView(topLever, language).action;
  const topAction = assessment.recommendedActions[0];
  if (topAction?.description || topAction?.title) {
    const actionView = getRecommendedActionView(topAction, language);
    return actionView.description || actionView.title;
  }
  return language === "en" ? "No critical next action was detected for this evaluation." : "Für diese Bewertung wurde aktuell keine kritische nächste Aktion erkannt.";
}

export function buildDealWinCrmExport(deal: DealWinDeal, assessment: DealWinAssessment, language: "de" | "en") {
  const valueContext = assessment.inputSnapshot?.hauffeValueContext;
  const customerValueScore = contextScoreValue(valueContext, ["customerValueScore"]);
  const customerFitScore = contextScoreValue(valueContext, ["customerFitScore"]);
  const segment = contextTextValue(valueContext, ["segment"]);
  const fitCategory = contextTextValue(valueContext, ["fitCategory"]);
  const assessedAt = contextTextValue(valueContext, ["assessedAt"]);
  return {
    hauffeOsDealId: deal.id,
    hauffeOsDealName: deal.title,
    hauffeOsExpectedCloseDate: deal.expectedCloseDate,
    hauffeOsEstimatedDealValue: deal.dealValue,
    hauffeOsWinProbability: assessment.winProbability,
    hauffeOsDealScore: assessment.scoreTotal,
    hauffeOsRiskLevel: assessment.riskLevel,
    hauffeOsRiskLevelLabel: labelFor(riskLevelLabels, language, assessment.riskLevel),
    hauffeOsConfidenceLevel: assessment.confidenceLevel,
    hauffeOsConfidenceLevelLabel: labelFor(confidenceLabels, language, assessment.confidenceLevel),
    hauffeOsNextBestAction: getNextBestAction(assessment, language),
    hauffeOsTopImprovementLevers: sortImprovementLevers(assessment.improvementLevers).slice(0, 3).map((lever) => ({
      key: lever.leverKey,
      title: getLeverView(lever, language).title,
      impact: positiveNumber(lever.estimatedScoreImpact)
    })),
    hauffeOsAssessmentDate: assessment.createdAt,
    hauffeOsAssessmentVersion: getAssessmentVersionLabel(assessment.assessmentVersion, language),
    ...(customerValueScore !== undefined ? { hauffeOsCustomerValueScore: customerValueScore } : {}),
    ...(customerFitScore !== undefined ? { hauffeOsCustomerFitScore: customerFitScore } : {}),
    ...(segment ? { hauffeOsCustomerValueSegment: segment } : {}),
    ...(fitCategory ? { hauffeOsCustomerValueFitCategory: fitCategory } : {}),
    ...(assessedAt ? { hauffeOsCustomerValueAssessedAt: assessedAt } : {})
  };
}

export function buildDealWinCrmNote(deal: DealWinDeal, assessment: DealWinAssessment, language: "de" | "en"): string {
  const exportData = buildDealWinCrmExport(deal, assessment, language);
  const topLevers = exportData.hauffeOsTopImprovementLevers
    .map((lever, index) => `${index + 1}. ${lever.title} (+${lever.impact})`)
    .join("\n");

  if (language === "en") {
    return [
      "Hauffe OS Deal Rating",
      `Deal: ${exportData.hauffeOsDealName}`,
      `Win probability: ${exportData.hauffeOsWinProbability}%`,
      `Deal quality score: ${exportData.hauffeOsDealScore}`,
      `Risk level: ${exportData.hauffeOsRiskLevelLabel}`,
      `Confidence: ${exportData.hauffeOsConfidenceLevelLabel}`,
      `Customer value in assessment: ${hasHauffeValueContext(assessment.inputSnapshot?.hauffeValueContext) ? "included" : "not linked yet"}`,
      `Next best action: ${exportData.hauffeOsNextBestAction}`,
      "Top improvement levers:",
      topLevers || "-",
      `Evaluation date: ${exportData.hauffeOsAssessmentDate}`
    ].join("\n");
  }

  return [
    "Hauffe OS Deal-Bewertung",
    `Deal: ${exportData.hauffeOsDealName}`,
    `Abschlusswahrscheinlichkeit: ${exportData.hauffeOsWinProbability}%`,
    `Deal-Qualitätsscore: ${exportData.hauffeOsDealScore}`,
    `Risikostufe: ${exportData.hauffeOsRiskLevelLabel}`,
    `Konfidenz: ${exportData.hauffeOsConfidenceLevelLabel}`,
    `Kundenwert in Bewertung: ${hasHauffeValueContext(assessment.inputSnapshot?.hauffeValueContext) ? "einbezogen" : "noch nicht verknüpft"}`,
    `Nächste beste Aktion: ${exportData.hauffeOsNextBestAction}`,
    "Top-Hebel:",
    topLevers || "-",
    `Bewertungsdatum: ${exportData.hauffeOsAssessmentDate}`
  ].join("\n");
}

function formatDealValue(deal: DealWinDeal, language: "de" | "en"): string {
  return `${deal.dealValue.toLocaleString(language === "en" ? "en-US" : "de-DE")} ${deal.currency}`;
}

function DealReviewHeader({
  deal,
  assessment,
  labels,
  language
}: {
  deal: DealWinDeal;
  assessment?: DealWinAssessment;
  labels: ReturnType<typeof copy>;
  language: "de" | "en";
}) {
  const topRisk = assessment?.riskReasons[0];
  const topLever = assessment ? sortImprovementLevers(assessment.improvementLevers)[0] : undefined;
  const topRiskView = topRisk ? getRiskView(topRisk, language) : undefined;
  const topLeverView = topLever ? getLeverView(topLever, language) : undefined;
  const contactProfileHint = getContactProfileReviewHint(deal.hauffeValueContext, language);

  return (
    <section className="deal-review-hero">
      <div className="deal-review-hero__intro">
        <span className="eyebrow">{labels.reviewSummary}</span>
        <h2>{deal.title}</h2>
        <p>{deal.companyName}</p>
      </div>
      <div className="deal-review-kpi-grid">
        <Stat label={labels.dealValueLabel} value={formatDealValue(deal, language)} />
        <Stat label={labels.winProbability} value={assessment ? `${assessment.winProbability}%` : "-"} />
        <Stat label={labels.expectedSigning} value={deal.expectedCloseDate} />
        <Stat label={labels.dealStatus} value={labelFor(dealStatusLabels, language, deal.status)} />
        <Stat label={labels.riskLevel} value={assessment ? labelFor(riskLevelLabels, language, assessment.riskLevel) : "-"} tone={assessment ? riskTone(assessment.riskLevel) : undefined} />
      </div>
      <div className="deal-review-hints">
        <span>{labels.dealValueMeaning}</span>
        <span>{labels.winProbabilityMeaning}</span>
        <span>{labels.expectedSigningMeaning}</span>
        <span>{labels.statusMeaning}</span>
      </div>
      <div className="deal-current-review-card">
        <h3>{labels.latestAssessment}</h3>
        {assessment ? (
          <div className="deal-current-review-card__content">
            <p>{getAssessmentInterpretation(assessment.winProbability, language)}</p>
            <div className="data-list">
              <div><span>{labels.biggestRisk}</span><strong>{topRiskView?.title ?? labels.noRisks}</strong></div>
              <div><span>{labels.strongestLever}</span><strong>{topLeverView?.title ?? labels.noNextAction}</strong></div>
              <div><span>{labels.lastAssessed}</span><strong>{assessment.createdAt}</strong></div>
            </div>
            {contactProfileHint ? (
              <p className="deal-profile-review-hint"><strong>{labels.contactProfileReviewHint}:</strong> {contactProfileHint}</p>
            ) : null}
          </div>
        ) : (
          <p>{labels.noAssessmentDetail}</p>
        )}
      </div>
    </section>
  );
}

function DealForm({
  value,
  labels,
  language,
  onChange,
  onSubmit,
  submitLabel,
  isSubmitting
}: {
  value: DealWinDealInput;
  labels: ReturnType<typeof copy>;
  language: "de" | "en";
  onChange: (next: DealWinDealInput) => void;
  onSubmit: () => void;
  submitLabel: string;
  isSubmitting: boolean;
}) {
  const update = <K extends keyof DealWinDealInput>(key: K, nextValue: DealWinDealInput[K]) => onChange({ ...value, [key]: nextValue });
  const updateCustomerValueScore = (nextValue: string) => {
    const score = nextValue.trim() === "" ? undefined : Number(nextValue);
    onChange({
      ...value,
      hauffeValueContext: {
        ...(value.hauffeValueContext ?? {}),
        customerValueScore: Number.isFinite(score) ? score : undefined
      }
    });
  };
  const profile: DealWinContactDecisionProfile = value.hauffeValueContext?.contactDecisionProfile ?? {
    version: "1.0",
    source: "manual",
    confidence: "low",
    riskTriggers: [],
    proofPreference: [],
    recommendedApproach: []
  };
  const updateContactProfile = (nextProfile: DealWinContactDecisionProfile) => {
    onChange({
      ...value,
      hauffeValueContext: {
        ...(value.hauffeValueContext ?? {}),
        contactDecisionProfile: nextProfile
      }
    });
  };
  return (
    <form className="form form-grid" onSubmit={(event) => { event.preventDefault(); onSubmit(); }}>
      <h3 className="form-section-title">{labels.formSections.dealContext}</h3>
      <label>{labels.fields.title}<input required value={value.title} onChange={(event) => update("title", event.target.value)} /></label>
      <label>{labels.fields.companyName}<input required value={value.companyName} onChange={(event) => update("companyName", event.target.value)} /></label>
      <label>{labels.fields.dealValue}<input required type="number" min="0" value={value.dealValue} onChange={(event) => update("dealValue", Number(event.target.value))} /></label>
      <label>{labels.fields.currency}<input required value={value.currency} onChange={(event) => update("currency", event.target.value)} /></label>
      <label>{labels.fields.salesStage}<select value={value.salesStage} onChange={(event) => update("salesStage", event.target.value as DealWinSalesStage)}>{salesStages.map((stage) => <option key={stage} value={stage}>{labelFor(salesStageLabels, language, stage)}</option>)}</select></label>
      <label>{labels.fields.status}<select value={value.status} onChange={(event) => update("status", event.target.value as DealWinDealStatus)}>{dealStatuses.map((status) => <option key={status} value={status}>{labelFor(dealStatusLabels, language, status)}</option>)}</select></label>
      <label>{labels.fields.expectedCloseDate}<input required type="date" value={value.expectedCloseDate} onChange={(event) => update("expectedCloseDate", event.target.value)} /></label>
      <label>{labels.fields.ownerName}<input value={value.ownerName ?? ""} onChange={(event) => update("ownerName", event.target.value)} /></label>
      <label>{labels.fields.contactName}<input value={value.contactName ?? ""} onChange={(event) => update("contactName", event.target.value)} /></label>
      <label>{labels.fields.contactEmail}<input type="email" value={value.contactEmail ?? ""} onChange={(event) => update("contactEmail", event.target.value)} /></label>
      <label>{labels.fields.industry}<input value={value.industry ?? ""} onChange={(event) => update("industry", event.target.value)} /></label>
      <label>{labels.fields.companySize}<input value={value.companySize ?? ""} onChange={(event) => update("companySize", event.target.value)} /></label>
      <h3 className="form-section-title">{labels.formSections.decisionQuality}</h3>
      <label>{labels.fields.decisionMakerAccess}<select value={normalizeDecisionMakerAccessValue(value.decisionMakerAccess)} onChange={(event) => update("decisionMakerAccess", event.target.value)}>
        <option value="">{labels.unknown}</option>
        <option value="yes">{labels.decisionMakerAccessOptions.yes}</option>
        <option value="no">{labels.decisionMakerAccessOptions.no}</option>
        <option value="unknown">{labels.decisionMakerAccessOptions.unknown}</option>
      </select></label>
      <label>{labels.fields.urgencyLevel}<select value={value.urgencyLevel ?? ""} onChange={(event) => update("urgencyLevel", event.target.value ? event.target.value as "low" | "medium" | "high" : undefined)}><option value="">{labels.unknown}</option><option value="low">{labelFor(priorityLabels, language, "low")}</option><option value="medium">{labelFor(priorityLabels, language, "medium")}</option><option value="high">{labelFor(priorityLabels, language, "high")}</option></select></label>
      {(["buyingCenterKnown", "budgetConfirmed", "painConfirmed", "competitorKnown"] as const).map((key) => (
        <label key={key}>{labels.fields[key]}<select value={booleanValue(value[key])} onChange={(event) => update(key, parseBoolean(event.target.value))}><option value="">{labels.unknown}</option><option value="true">{labels.yes}</option><option value="false">{labels.no}</option></select></label>
      ))}
      <label>{labels.fields.offerSentAt}<input type="date" value={value.offerSentAt ?? ""} onChange={(event) => update("offerSentAt", event.target.value)} /></label>
      <label>{labels.fields.lastActivityAt}<input type="date" value={value.lastActivityAt ?? ""} onChange={(event) => update("lastActivityAt", event.target.value)} /></label>
      <h3 className="form-section-title">{labels.formSections.customerValueFit}</h3>
      <label>{labels.fields.customerValueScore}<input type="number" min="0" max="100" value={value.hauffeValueContext?.customerValueScore ?? ""} onChange={(event) => updateCustomerValueScore(event.target.value)} /><small>{labels.customerValueScoreHelp}</small></label>
      <fieldset className="form-wide deal-win-contact-profile">
        <legend>{labels.contactProfileTitle}</legend>
        <p className="form-note">{labels.contactProfileDescription}</p>
        <div className="form form-grid">
          <label>{labels.contactProfileLabels.source}<select value={profile.source} onChange={(event) => updateContactProfile({ ...profile, source: event.target.value as DealWinContactDecisionProfile["source"] })}>
            {(["manual", "crm", "inferred"] as const).map((source) => <option key={source} value={source}>{labels.contactProfileSources[source]}</option>)}
          </select></label>
          <label>{labels.contactProfileLabels.confidence}<select value={profile.confidence} onChange={(event) => updateContactProfile({ ...profile, confidence: event.target.value as DealWinContactDecisionProfile["confidence"] })}>
            {(["low", "medium", "high"] as const).map((confidence) => <option key={confidence} value={confidence}>{labelFor(confidenceLabels, language, confidence)}</option>)}
          </select></label>
          <label>{labels.contactProfileLabels.decisionStyle}<select value={profile.decisionStyle ?? ""} onChange={(event) => updateContactProfile({ ...profile, decisionStyle: event.target.value ? event.target.value as DealWinDecisionStyle : undefined })}>
            <option value="">{labels.unknown}</option>
            {decisionStyleOptions.map((style) => <option key={style} value={style}>{labels.contactProfileDecisionStyles[style]}</option>)}
          </select></label>
          <label>{labels.contactProfileLabels.communicationPreference}<select value={profile.communicationPreference ?? ""} onChange={(event) => updateContactProfile({ ...profile, communicationPreference: event.target.value ? event.target.value as DealWinCommunicationPreference : undefined })}>
            <option value="">{labels.unknown}</option>
            {communicationPreferenceOptions.map((preference) => <option key={preference} value={preference}>{labels.contactProfileCommunicationPreferences[preference]}</option>)}
          </select></label>
        </div>
        <div className="form-grid">
          <section className="profile-option-section">
            <strong>{labels.contactProfileLabels.riskTriggers}</strong>
            <p>{labels.contactProfileSectionInstructions.riskTriggers}</p>
            <div className="profile-option-grid">
              {riskTriggerOptions.map((item) => (
                <button key={item} type="button" className={`profile-option-card${profile.riskTriggers.includes(item) ? " is-selected" : ""}`} aria-pressed={profile.riskTriggers.includes(item)} onClick={() => updateContactProfile({ ...profile, riskTriggers: toggleListValue(profile.riskTriggers, item) })}>
                  {profile.riskTriggers.includes(item) ? <span>{labels.contactProfileSelected}</span> : null}
                  <strong>{labels.contactProfileRiskTriggers[item]}</strong>
                  <small>{labels.contactProfileRiskTriggerDescriptions[item]}</small>
                </button>
              ))}
            </div>
          </section>
          <section className="profile-option-section">
            <strong>{labels.contactProfileLabels.proofPreference}</strong>
            <p>{labels.contactProfileSectionInstructions.proofPreference}</p>
            <div className="profile-option-grid">
              {proofPreferenceOptions.map((item) => (
                <button key={item} type="button" className={`profile-option-card${profile.proofPreference.includes(item) ? " is-selected" : ""}`} aria-pressed={profile.proofPreference.includes(item)} onClick={() => updateContactProfile({ ...profile, proofPreference: toggleListValue(profile.proofPreference, item) })}>
                  {profile.proofPreference.includes(item) ? <span>{labels.contactProfileSelected}</span> : null}
                  <strong>{labels.contactProfileProofPreferences[item]}</strong>
                  <small>{labels.contactProfileProofPreferenceDescriptions[item]}</small>
                </button>
              ))}
            </div>
          </section>
          <section className="form-wide profile-option-section">
            <strong>{labels.contactProfileLabels.recommendedApproach}</strong>
            <p>{labels.contactProfileSectionInstructions.recommendedApproach}</p>
            <div className="profile-option-grid profile-option-grid-wide">
              {recommendedApproachOptions.map((item) => (
                <button key={item} type="button" className={`profile-option-card${profile.recommendedApproach.includes(item) ? " is-selected" : ""}`} aria-pressed={profile.recommendedApproach.includes(item)} onClick={() => updateContactProfile({ ...profile, recommendedApproach: toggleListValue(profile.recommendedApproach, item) })}>
                  {profile.recommendedApproach.includes(item) ? <span>{labels.contactProfileSelected}</span> : null}
                  <strong>{labels.contactProfileRecommendedApproaches[item]}</strong>
                  <small>{labels.contactProfileRecommendedApproachDescriptions[item]}</small>
                </button>
              ))}
            </div>
          </section>
        </div>
        <label>{labels.contactProfileLabels.notes}<textarea rows={3} value={profile.notes ?? ""} onChange={(event) => updateContactProfile({ ...profile, notes: event.target.value })} /><small>{labels.contactProfileHelp}</small></label>
      </fieldset>
      <label className="form-wide">{labels.fields.nextStep}<input value={value.nextStep ?? ""} onChange={(event) => update("nextStep", event.target.value)} /></label>
      <label className="form-wide">{labels.fields.notes}<textarea rows={4} value={value.notes ?? ""} onChange={(event) => update("notes", event.target.value)} /></label>
      <button className="button button-primary" type="submit" disabled={isSubmitting}>{submitLabel}</button>
    </form>
  );
}

function AssessmentView({
  assessment,
  deal,
  labels,
  language,
  onAssess,
  isAssessing
}: {
  assessment?: DealWinAssessment;
  deal?: DealWinDeal;
  labels: ReturnType<typeof copy>;
  language: "de" | "en";
  onAssess: () => void;
  isAssessing: boolean;
}) {
  const [selectedLeverKeys, setSelectedLeverKeys] = useState<string[]>([]);
  const [copyMessage, setCopyMessage] = useState<string | undefined>();

  if (!assessment) {
    return (
      <div className="deal-win-empty-assessment">
        <p className="muted">{labels.noAssessmentGuidance}</p>
        <button className="button button-primary" type="button" onClick={onAssess} disabled={isAssessing}>{labels.assessNow}</button>
      </div>
    );
  }

  const topLevers = sortImprovementLevers(assessment.improvementLevers).slice(0, 3);
  const simulation = calculateWhatIfSimulation(assessment, selectedLeverKeys);
  const scoreCapReached = simulation.simulatedScore >= 100;
  const showBelowMaxExplanation = simulation.selected.length > 0 && simulation.simulatedScore < 100;
  const showSingleLeverExplanation = assessment.improvementLevers.length === 1;
  const interpretation = getAssessmentInterpretation(assessment.winProbability, language);
  const nextBestAction = getNextBestAction(assessment, language);
  const crmExport = deal ? buildDealWinCrmExport(deal, assessment, language) : undefined;
  const crmJson = crmExport ? JSON.stringify(crmExport, null, 2) : "";
  const crmNote = deal ? buildDealWinCrmNote(deal, assessment, language) : "";
  const valueContext = assessment.inputSnapshot?.hauffeValueContext;
  const valueContextAvailable = hasHauffeValueContext(valueContext);
  const customerValueScore = contextScoreValue(valueContext, ["customerValueScore"]);
  const customerFitScore = contextScoreValue(valueContext, ["customerFitScore"]);
  const commercialFitScore = contextScoreValue(valueContext, ["commercialFitScore", "profitabilityScore", "marginPotentialScore"]);
  const valueSegment = contextTextValue(valueContext, ["segment", "fitCategory"]);
  const valueAssessedAt = contextTextValue(valueContext, ["assessedAt"]);
  const toggleLever = (lever: DealWinImprovementLever) => {
    setSelectedLeverKeys((current) => current.includes(lever.leverKey) ? current.filter((key) => key !== lever.leverKey) : [...current, lever.leverKey]);
  };
  const copyText = async (text: string) => {
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
      setCopyMessage(labels.copyUnavailable);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopyMessage(labels.copied);
    } catch {
      setCopyMessage(labels.copyUnavailable);
    }
  };

  return (
    <div className="deal-win-assessment">
      <section className="deal-win-summary-card">
        <div className="deal-win-summary-card__main">
          <span className="eyebrow">{labels.latestAssessment}</span>
          <strong>{assessment.winProbability}%</strong>
          <p>{interpretation}</p>
        </div>
        <div className="deal-win-summary-card__stats">
          <Stat label={labels.dealScore} value={`${assessment.scoreTotal}`} />
          <Stat label={labels.riskLevel} value={labelFor(riskLevelLabels, language, assessment.riskLevel)} tone={riskTone(assessment.riskLevel)} />
          <Stat label={labels.confidence} value={labelFor(confidenceLabels, language, assessment.confidenceLevel)} />
          <Stat label={labels.assessmentDate} value={assessment.createdAt} />
        </div>
        <p className="form-note">{labels.assessmentVersion}: {getAssessmentVersionLabel(assessment.assessmentVersion, language)}</p>
      </section>

      <Panel title={labels.nextBestAction} variant="accent">
        <p className="deal-win-next-action">{nextBestAction || labels.noNextAction}</p>
      </Panel>

      <Panel title={valueContextAvailable ? labels.customerValueContextUsed : labels.customerValueContextMissing} variant="soft">
        {valueContextAvailable ? (
          <div className="data-list">
            {customerValueScore !== undefined ? <div><span>{labels.customerValueScore}</span><strong>{customerValueScore}</strong></div> : null}
            {customerFitScore !== undefined ? <div><span>{labels.customerFitScore}</span><strong>{customerFitScore}</strong></div> : null}
            {commercialFitScore !== undefined ? <div><span>{labels.customerValueCommercialFit}</span><strong>{commercialFitScore}</strong></div> : null}
            {valueSegment ? <div><span>{labels.customerValueSegment}</span><strong>{valueSegment}</strong></div> : null}
            {valueAssessedAt ? <div><span>{labels.customerValueAssessedAt}</span><strong>{valueAssessedAt}</strong></div> : null}
          </div>
        ) : (
          <p>{labels.customerValueContextMissingDescription}</p>
        )}
      </Panel>

      {topLevers.length ? (
        <Panel title={labels.topLevers} variant="accent">
          <div className="deal-win-lever-grid">
            {topLevers.map((lever) => (
              <article key={lever.leverKey} className="deal-win-lever-card">
                <div className="deal-win-lever-card__head">
                  <strong>{getLeverView(lever, language).title}</strong>
                  <StatusBadge>{labelFor(priorityLabels, language, lever.priority)}</StatusBadge>
                </div>
                <div className="deal-win-impact-row">
                  <span>{language === "en" ? "Modeled effect" : "Modellierter Effekt"}: +{positiveNumber(lever.estimatedScoreImpact)} {language === "en" ? "points" : "Punkte"}</span>
                </div>
                <p>{getLeverView(lever, language).action}</p>
                <small>{getDimensionLabel(lever.affectedScoreDimension, lever.affectedScoreDimension, language)}</small>
              </article>
            ))}
          </div>
        </Panel>
      ) : null}

      {assessment.improvementLevers.length ? (
        <Panel title={labels.whatIf} variant="soft">
          <div className="deal-win-simulation">
            <div className="deal-win-simulation__result">
              <span>{labels.winProbability}</span>
              <strong>{assessment.winProbability}% → {simulation.simulatedWinProbability}%</strong>
              <small>{labels.selectedProbabilityImpact}: +{simulation.probabilityImpact} {language === "en" ? "points" : "Punkte"}</small>
            </div>
            <div className="deal-win-simulation__result">
              <span>{labels.dealScore}</span>
              <strong>{assessment.scoreTotal} → {simulation.simulatedScore}</strong>
              <small>{labels.selectedScoreImpact}: +{simulation.scoreImpact} {language === "en" ? "points" : "Punkte"}</small>
            </div>
          </div>
          <p className="form-note">{labels.whatIfScoreGuaranteeNote}</p>
          <p className="form-note">{labels.whatIfNonAdditiveNote}</p>
          {scoreCapReached ? <p className="deal-win-score-cap-note">{labels.whatIfScoreCapReached}</p> : null}
          {showBelowMaxExplanation ? <p className="deal-win-score-cap-note">{labels.whatIfBelowMaxNote}</p> : null}
          {showSingleLeverExplanation ? <p className="deal-win-score-cap-note">{labels.whatIfSingleLeverNote}</p> : null}
          <div className="deal-win-check-list">
            {assessment.improvementLevers.map((lever) => (
              <label key={lever.leverKey} className="deal-win-check-card">
                <input type="checkbox" checked={selectedLeverKeys.includes(lever.leverKey)} onChange={() => toggleLever(lever)} />
                <span>
                  <strong>{getLeverView(lever, language).title}</strong>
                  <small>{language === "en" ? "Single modeled effect" : "Einzeleffekt im Modell"}: +{positiveNumber(lever.estimatedScoreImpact)} {language === "en" ? "points" : "Punkte"}</small>
                </span>
              </label>
            ))}
          </div>
          <p className="form-note">{simulation.selected.length ? `${labels.selectedLevers}: ${simulation.selected.map((lever) => getLeverView(lever, language).title).join(", ")}` : labels.noSelectedLevers}</p>
          <p className="form-note">{labels.simulationNote}</p>
          <button className="button button-secondary" type="button" onClick={onAssess} disabled={isAssessing}>{labels.updateAndReassess}</button>
        </Panel>
      ) : null}

      <Panel title={labels.scoreBreakdown} variant="soft">
        <div className="deal-win-list">
          {assessment.scoreBreakdown.map((item) => {
            const scoreWidth = Math.min(100, Math.max(0, positiveNumber(item.score) * 10));
            return (
              <article key={item.key ?? item.dimension} className="deal-win-row deal-win-score-row">
                <div><strong>{getDimensionLabel(item.key ?? item.dimension, item.label ?? item.dimension, language)}</strong><span>{item.weight}% · {language === "en" ? "Score" : "Wertung"} {item.score}</span></div>
                <div className="deal-win-score-bar" aria-hidden="true"><span style={{ width: `${scoreWidth}%` }} /></div>
                <p>{item.reasons?.map((reason) => translateReason(reason, language)).join(" ")}</p>
              </article>
            );
          })}
        </div>
      </Panel>

      <div className="deal-win-split">
        <Panel title={labels.risks} variant="soft">
          <div className="deal-win-list">
            {assessment.riskReasons.length ? assessment.riskReasons.map((risk) => {
              const riskView = getRiskView(risk, language);
              return (
                <article key={risk.riskKey ?? risk.riskTitle} className="deal-win-row">
                  <div><strong>{riskView.title}</strong><StatusBadge tone={riskTone(risk.severity)}>{labelFor(riskLevelLabels, language, risk.severity)}</StatusBadge></div>
                  <p>{riskView.description}</p>
                  {riskView.action ? <small>{riskView.action}</small> : null}
                </article>
              );
            }) : <p className="muted">{labels.noRisks}</p>}
          </div>
        </Panel>

        <Panel title={labels.actions} variant="soft">
          <div className="deal-win-list">
            {assessment.recommendedActions.map((action) => {
              const actionView = getRecommendedActionView(action, language);
              return (
                <article key={action.actionKey ?? action.title} className="deal-win-row">
                  <div><strong>{actionView.title}</strong><StatusBadge>{labelFor(priorityLabels, language, action.priority ?? "medium")}</StatusBadge></div>
                  <p>{actionView.description}</p>
                </article>
              );
            })}
          </div>
        </Panel>
      </div>

      {hasContactDecisionProfile(valueContext) ? <p className="form-note">{labels.contactProfileQualitativeNote}</p> : null}

      {crmExport ? (
        <Panel title={labels.crmExport} variant="soft">
          <p>{labels.crmExportIntro}</p>
          <p className="form-note">{labels.noCrmSync}</p>
          <h3>{labels.crmFieldList}</h3>
          <div className="data-list">
            <div><span>{labels.winProbability}<small>hauffeOsWinProbability</small></span><strong>{crmExport.hauffeOsWinProbability}%</strong></div>
            <div><span>{labels.dealScore}<small>hauffeOsDealScore</small></span><strong>{crmExport.hauffeOsDealScore}</strong></div>
            <div><span>{labels.riskLevel}<small>hauffeOsRiskLevel</small></span><strong>{crmExport.hauffeOsRiskLevelLabel}</strong></div>
            <div><span>{labels.confidence}<small>hauffeOsConfidenceLevel</small></span><strong>{crmExport.hauffeOsConfidenceLevelLabel}</strong></div>
            <div><span>{labels.nextBestAction}<small>hauffeOsNextBestAction</small></span><strong>{crmExport.hauffeOsNextBestAction}</strong></div>
            <div><span>{labels.assessmentDate}<small>hauffeOsAssessmentDate</small></span><strong>{crmExport.hauffeOsAssessmentDate}</strong></div>
            {"hauffeOsCustomerValueScore" in crmExport ? <div><span>{labels.customerValueScore}<small>hauffeOsCustomerValueScore</small></span><strong>{crmExport.hauffeOsCustomerValueScore}</strong></div> : null}
            {"hauffeOsCustomerFitScore" in crmExport ? <div><span>{labels.customerFitScore}<small>hauffeOsCustomerFitScore</small></span><strong>{crmExport.hauffeOsCustomerFitScore}</strong></div> : null}
            {"hauffeOsCustomerValueSegment" in crmExport ? <div><span>{labels.customerValueSegment}<small>hauffeOsCustomerValueSegment</small></span><strong>{crmExport.hauffeOsCustomerValueSegment}</strong></div> : null}
            {"hauffeOsCustomerValueFitCategory" in crmExport ? <div><span>{labels.customerValueFitCategory}<small>hauffeOsCustomerValueFitCategory</small></span><strong>{crmExport.hauffeOsCustomerValueFitCategory}</strong></div> : null}
            {"hauffeOsCustomerValueAssessedAt" in crmExport ? <div><span>{labels.customerValueAssessedAt}<small>hauffeOsCustomerValueAssessedAt</small></span><strong>{crmExport.hauffeOsCustomerValueAssessedAt}</strong></div> : null}
          </div>
          <p className="form-note">{labels.crmJson}</p>
          <h3>{labels.crmNote}</h3>
          <pre className="code-block">{crmNote}</pre>
          <div className="form-actions">
            <button className="button button-secondary" type="button" onClick={() => void copyText(crmJson)}>{labels.copyJson}</button>
            <button className="button button-secondary" type="button" onClick={() => void copyText(crmNote)}>{labels.copyNote}</button>
          </div>
          {copyMessage ? <p className="form-note" role="status">{copyMessage}</p> : null}
        </Panel>
      ) : null}
    </div>
  );
}

export function DealWinEngine({ dealId }: { dealId?: string }) {
  const language = useWorkspaceLanguage();
  const labels = copy(language);
  const [deals, setDeals] = useState<DealWinDeal[]>([]);
  const [latestByDeal, setLatestByDeal] = useState<Record<string, DealWinAssessment | undefined>>({});
  const [deal, setDeal] = useState<DealWinDeal | undefined>();
  const [latest, setLatest] = useState<DealWinAssessment | undefined>();
  const [history, setHistory] = useState<DealWinAssessment[]>([]);
  const [form, setForm] = useState<DealWinDealInput>(initialForm);
  const [showCreate, setShowCreate] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  const loadOverview = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);
    try {
      const nextDeals = await fetchDealWinDeals();
      setDeals(nextDeals);
      const pairs = await Promise.all(nextDeals.map(async (item) => [item.id, await fetchLatestDealWinAssessment(item.id).catch(() => undefined)] as const));
      setLatestByDeal(Object.fromEntries(pairs));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : labels.loadError);
    } finally {
      setIsLoading(false);
    }
  }, [labels.loadError]);

  const loadDetail = useCallback(async () => {
    if (!dealId) return;
    setIsLoading(true);
    setError(undefined);
    try {
      const [nextDeal, nextLatest, nextHistory] = await Promise.all([
        fetchDealWinDeal(dealId),
        fetchLatestDealWinAssessment(dealId),
        fetchDealWinAssessments(dealId).catch(() => [])
      ]);
      setDeal(nextDeal);
      setForm(formFromDeal(nextDeal));
      setLatest(nextLatest);
      setHistory(nextHistory);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : labels.loadError);
    } finally {
      setIsLoading(false);
    }
  }, [dealId, labels.loadError]);

  useEffect(() => {
    if (dealId) void loadDetail();
    else void loadOverview();
  }, [dealId, loadDetail, loadOverview]);

  const sortedDeals = useMemo(() => deals.slice().sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)), [deals]);

  async function submitCreate() {
    setIsSaving(true);
    setError(undefined);
    try {
      const created = await createDealWinDeal(cleanForm(form));
      setMessage(labels.createSuccess);
      window.location.assign(withLanguagePrefix(`/workspace/deal-win-engine/${created.id}`, language));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : labels.loadError);
    } finally {
      setIsSaving(false);
    }
  }

  async function submitUpdate() {
    if (!dealId) return;
    setIsSaving(true);
    setError(undefined);
    try {
      const updated = await updateDealWinDeal(dealId, cleanForm(form));
      setDeal(updated);
      setIsEditing(false);
      setMessage(labels.updateSuccess);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : labels.loadError);
    } finally {
      setIsSaving(false);
    }
  }

  async function archiveCurrentDeal() {
    if (!dealId || !window.confirm(labels.archiveConfirm)) return;
    setIsSaving(true);
    setError(undefined);
    try {
      await archiveDealWinDeal(dealId);
      setMessage(labels.archiveSuccess);
      window.location.assign(withLanguagePrefix("/workspace/deal-win-engine", language));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : labels.loadError);
    } finally {
      setIsSaving(false);
    }
  }

  async function runAssessment() {
    if (!dealId) return;
    setIsSaving(true);
    setError(undefined);
    try {
      const nextAssessment = await assessDealWinDeal(dealId);
      setLatest(nextAssessment);
      setHistory([nextAssessment, ...history.filter((item) => item.id !== nextAssessment.id)].slice(0, 5));
      setMessage(labels.assessSuccess);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : labels.loadError);
    } finally {
      setIsSaving(false);
    }
  }

  async function importCustomerValueSnapshot() {
    if (!dealId) return;
    setIsSaving(true);
    setError(undefined);
    try {
      const result = await createDealWinCustomerValueSnapshot(dealId);
      setDeal(result.deal);
      setForm(formFromDeal(result.deal));
      setMessage(labels.customerValueSnapshotSuccess);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : labels.customerValueSnapshotError);
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <Layout eyebrow={labels.eyebrow} title={labels.title} description={labels.description}>
        <Panel title={labels.loading} variant="soft"><p>{labels.loading}</p></Panel>
      </Layout>
    );
  }

  return (
    <Layout eyebrow={labels.eyebrow} title={labels.title} description={labels.description}>
      {error ? <Panel title="Hinweis" variant="accent"><p className="form-error" role="alert">{error}</p></Panel> : null}
      {message ? <Panel title="Status" variant="soft"><p>{message}</p></Panel> : null}

      {!dealId ? (
        <>
          <Panel title={labels.overview}>
            <div className="hero-actions">
              <button className="button button-primary" type="button" onClick={() => { setShowCreate(!showCreate); setForm(initialForm); }}>
                {labels.newDeal}
              </button>
            </div>
            {showCreate ? (
              <div className="deal-win-form-wrap">
                <h3>{labels.formTitle}</h3>
                <DealForm value={form} labels={labels} language={language} onChange={setForm} onSubmit={submitCreate} submitLabel={labels.newDeal} isSubmitting={isSaving} />
              </div>
            ) : null}
            {!sortedDeals.length ? <p className="muted">{labels.empty}</p> : null}
            <div className="deal-win-card-grid">
              {sortedDeals.map((item) => {
                const itemLatest = latestByDeal[item.id];
                return (
                  <article key={item.id} className="deal-win-card">
                    <div>
                      <span className="eyebrow">{labelFor(salesStageLabels, language, item.salesStage)}</span>
                      <h2>{item.title}</h2>
                      <p>{item.companyName}</p>
                    </div>
                    <div className="deal-win-meta">
                      <span>{item.dealValue.toLocaleString(language === "en" ? "en-US" : "de-DE")} {item.currency}</span>
                      <span>{item.expectedCloseDate}</span>
                      <StatusBadge>{labelFor(dealStatusLabels, language, item.status)}</StatusBadge>
                    </div>
                    {itemLatest ? (
                      <div className="deal-win-meta">
                        <strong>{itemLatest.winProbability}%</strong>
                        <StatusBadge tone={riskTone(itemLatest.riskLevel)}>{labelFor(riskLevelLabels, language, itemLatest.riskLevel)}</StatusBadge>
                      </div>
                    ) : null}
                    <div className="form-actions">
                      <a className="button button-primary" href={withLanguagePrefix(`/workspace/deal-win-engine/${item.id}`, language)}>{labels.open}</a>
                    </div>
                  </article>
                );
              })}
            </div>
          </Panel>
        </>
      ) : (
        <>
          <a className="button button-secondary" href={withLanguagePrefix("/workspace/deal-win-engine", language)}>{labels.back}</a>
          {deal ? (
            <>
            <DealReviewHeader deal={deal} assessment={latest} labels={labels} language={language} />
            <Panel title={labels.details}>
              <div className="data-list">
                <div><span>{labels.fields.companyName}</span><strong>{deal.companyName}</strong></div>
                <div><span>{labels.fields.salesStage}</span><strong>{labelFor(salesStageLabels, language, deal.salesStage)}</strong></div>
                <div><span>{labels.fields.expectedCloseDate}</span><strong>{deal.expectedCloseDate}</strong></div>
                <div><span>{labels.fields.ownerName}</span><strong>{deal.ownerName || "-"}</strong></div>
                <div><span>{labels.fields.contactName}</span><strong>{deal.contactName || "-"}</strong></div>
                <div><span>{labels.fields.decisionMakerAccess}</span><strong>{deal.decisionMakerAccess ? labels.decisionMakerAccessOptions[normalizeDecisionMakerAccessValue(deal.decisionMakerAccess) || "unknown"] : "-"}</strong></div>
                <div><span>{labels.fields.nextStep}</span><strong>{deal.nextStep || "-"}</strong></div>
                <div><span>{labels.fields.notes}</span><strong>{deal.notes || "-"}</strong></div>
              </div>
              <Panel title={hasHauffeValueContext(deal.hauffeValueContext) ? labels.customerValueContextUsed : labels.customerValueContextMissing} variant="soft">
                {hasHauffeValueContext(deal.hauffeValueContext) ? (
                  <div className="data-list">
                    <div>
                      <span>{labels.customerValueContextStatus}</span>
                      <strong>{hasConcreteCustomerValueSegment(deal.hauffeValueContext) ? labels.customerValueConcreteSegment : labels.customerValueModelOnly}</strong>
                    </div>
                    {deal.hauffeValueContext.segment ? <div><span>{labels.customerValueSegment}</span><strong>{deal.hauffeValueContext.segment}</strong></div> : null}
                    {deal.hauffeValueContext.customerValueScore !== undefined ? <div><span>{labels.customerValueScore}</span><strong>{deal.hauffeValueContext.customerValueScore}</strong></div> : null}
                    {deal.hauffeValueContext.criteriaCount !== undefined ? <div><span>{labels.customerValueCriteriaCount}</span><strong>{deal.hauffeValueContext.criteriaCount}</strong></div> : null}
                  </div>
                ) : (
                  <p>{labels.customerValueContextMissingDescription}</p>
                )}
                <div className="form-actions">
                  <button className="button button-secondary" type="button" onClick={() => void importCustomerValueSnapshot()} disabled={isSaving}>
                    {hasHauffeValueContext(deal.hauffeValueContext) ? labels.updateCustomerValueSnapshot : labels.importCustomerValueSnapshot}
                  </button>
                </div>
                <p className="form-note">{labels.customerValueSnapshotNote}</p>
              </Panel>
              <Panel title={labels.contactProfileTitle} variant="soft">
                {hasContactDecisionProfile(deal.hauffeValueContext) ? (
                  <div className="data-list">
                    <div><span>{labels.contactProfileHypothesis}</span><strong>{labels.contactProfileSummary}</strong></div>
                    <div><span>{labels.contactProfileLabels.source}</span><strong>{labels.contactProfileSources[deal.hauffeValueContext.contactDecisionProfile.source]}</strong></div>
                    <div><span>{labels.contactProfileLabels.confidence}</span><strong>{labelFor(confidenceLabels, language, deal.hauffeValueContext.contactDecisionProfile.confidence)}</strong></div>
                    {deal.hauffeValueContext.contactDecisionProfile.decisionStyle ? <div><span>{labels.contactProfileLabels.decisionStyle}</span><strong>{labels.contactProfileDecisionStyles[deal.hauffeValueContext.contactDecisionProfile.decisionStyle]}</strong></div> : null}
                    {deal.hauffeValueContext.contactDecisionProfile.communicationPreference ? <div><span>{labels.contactProfileLabels.communicationPreference}</span><strong>{labels.contactProfileCommunicationPreferences[deal.hauffeValueContext.contactDecisionProfile.communicationPreference]}</strong></div> : null}
                    {deal.hauffeValueContext.contactDecisionProfile.riskTriggers.length ? <div><span>{labels.contactProfileLabels.riskTriggers}</span><strong>{deal.hauffeValueContext.contactDecisionProfile.riskTriggers.map((item) => labels.contactProfileRiskTriggers[item]).join(", ")}</strong></div> : null}
                    {deal.hauffeValueContext.contactDecisionProfile.proofPreference.length ? <div><span>{labels.contactProfileLabels.proofPreference}</span><strong>{deal.hauffeValueContext.contactDecisionProfile.proofPreference.map((item) => labels.contactProfileProofPreferences[item]).join(", ")}</strong></div> : null}
                    {deal.hauffeValueContext.contactDecisionProfile.recommendedApproach.length ? <div><span>{labels.contactProfileLabels.recommendedApproach}</span><strong>{deal.hauffeValueContext.contactDecisionProfile.recommendedApproach.map((item) => labels.contactProfileRecommendedApproaches[item]).join(", ")}</strong></div> : null}
                    {deal.hauffeValueContext.contactDecisionProfile.notes ? <div><span>{labels.contactProfileLabels.notes}</span><strong>{deal.hauffeValueContext.contactDecisionProfile.notes}</strong></div> : null}
                  </div>
                ) : (
                  <p>{labels.contactProfileMissing}</p>
                )}
                <p className="form-note">{labels.contactProfileDescription}</p>
              </Panel>
              <div className="form-actions">
                <button className="button button-primary" type="button" onClick={() => void runAssessment()} disabled={isSaving}>{labels.assess}</button>
                <button className="button button-secondary" type="button" onClick={() => setIsEditing(!isEditing)}>{labels.edit}</button>
                <button className="button button-secondary" type="button" onClick={() => void archiveCurrentDeal()} disabled={isSaving}>{labels.archive}</button>
              </div>
              <p className="form-note">{labels.reassessNote}</p>
            </Panel>
            </>
          ) : null}

          {isEditing ? (
            <Panel title={labels.edit} variant="soft">
              <DealForm value={form} labels={labels} language={language} onChange={setForm} onSubmit={submitUpdate} submitLabel={labels.save} isSubmitting={isSaving} />
              <button className="button button-secondary" type="button" onClick={() => setIsEditing(false)}>{labels.cancel}</button>
            </Panel>
          ) : null}

          <Panel variant="accent">
            <AssessmentView assessment={latest} deal={deal} labels={labels} language={language} onAssess={() => void runAssessment()} isAssessing={isSaving} />
          </Panel>

          {history.length ? (
            <Panel title={labels.history} variant="soft">
              <div className="deal-win-list">
                {history.slice(0, 5).map((item) => (
                  <article key={item.id} className="deal-win-row">
                    <div><strong>{item.createdAt}</strong><span>{item.scoreTotal} · {item.winProbability}% · {labelFor(riskLevelLabels, language, item.riskLevel)}</span></div>
                  </article>
                ))}
              </div>
            </Panel>
          ) : null}
        </>
      )}
    </Layout>
  );
}
